var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import '../styles/widgets/arrayModel.scss';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import { ColorEnum } from '../components/ColorPicker';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { selectTool } from '../helper/helper';
import { playAudio } from '../helper/audioHelper';
import { useTranslation } from 'react-i18next';
import { announce } from '../helper/announcer';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import SelectFocusOverlay from '../components/SelectFocusOverlay';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var DEFAULT_WORKSPACE_CELL_SIZE = 55;
var PREVIEW_SIZE = 320;
var OPEN_SIZE = DEFAULT_WORKSPACE_CELL_SIZE * 5;
// Pure utility — lives outside the component so useCallback never captures a stale reference.
// When a cut is removed at cutIdx (0-based in sorted cut array):
//   absorbing segment (cutIdx) keeps its color.
//   absorbed segment (cutIdx+1) inherits to merged position only if absorbing was empty.
//   Both segments lose color if absorbing already had one (absorbing wins).
//   Segments beyond cutIdx+1 shift their index down by 1.
function remapColorsAfterCutRemoval(oldColors, cutType, cutIdx) {
    if (!oldColors || cutIdx === -1)
        return oldColors !== null && oldColors !== void 0 ? oldColors : {};
    var newColors = {};
    for (var _i = 0, _a = Object.entries(oldColors); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], color = _b[1];
        var _c = key.split('-'), rStr = _c[0], cStr = _c[1];
        var rIdx = parseInt(rStr, 10);
        var cIdx = parseInt(cStr, 10);
        if (cutType === 'row') {
            if (rIdx < cutIdx) {
                newColors[key] = color; // before merge — unchanged
            }
            else if (rIdx === cutIdx) {
                newColors[key] = color; // absorbing — keep
            }
            else if (rIdx === cutIdx + 1) {
                // absorbed — inherit only if absorbing side had no color for this column
                if (!oldColors["".concat(cutIdx, "-").concat(cIdx)]) {
                    newColors["".concat(cutIdx, "-").concat(cIdx)] = color;
                }
            }
            else {
                newColors["".concat(rIdx - 1, "-").concat(cIdx)] = color; // beyond merge — shift up
            }
        }
        else {
            if (cIdx < cutIdx) {
                newColors[key] = color; // before merge — unchanged
            }
            else if (cIdx === cutIdx) {
                newColors[key] = color; // absorbing — keep
            }
            else if (cIdx === cutIdx + 1) {
                // absorbed — inherit only if absorbing side had no color for this row
                if (!oldColors["".concat(rIdx, "-").concat(cutIdx)]) {
                    newColors["".concat(rIdx, "-").concat(cutIdx)] = color;
                }
            }
            else {
                newColors["".concat(rIdx, "-").concat(cIdx - 1)] = color; // beyond merge — shift left
            }
        }
    }
    return newColors;
}
// When a new cut is inserted at sorted index `insertPos`:
//   - Sections before the cut (index < insertPos): unchanged.
//   - The section being split (index === insertPos): both the first half (same key)
//     AND the second half (key+1) inherit the original color so the filled area
//     remains visually continuous — the cut line appears on top of the fill.
//   - Sections after the cut (index > insertPos): shift up by 1.
function remapColorsAfterCutAddition(oldColors, cutType, insertPos) {
    if (!oldColors)
        return {};
    var newColors = {};
    for (var _i = 0, _a = Object.entries(oldColors); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], color = _b[1];
        var _c = key.split('-'), rStr = _c[0], cStr = _c[1];
        var rIdx = parseInt(rStr, 10);
        var cIdx = parseInt(cStr, 10);
        if (cutType === 'row') {
            if (rIdx < insertPos) {
                newColors[key] = color; // before split — unchanged
            }
            else if (rIdx === insertPos) {
                newColors[key] = color; // first half of split — keep color
                newColors["".concat(rIdx + 1, "-").concat(cIdx)] = color; // second half of split — inherit color
            }
            else {
                newColors["".concat(rIdx + 1, "-").concat(cIdx)] = color; // after new cut — shift down
            }
        }
        else {
            if (cIdx < insertPos) {
                newColors[key] = color; // before split — unchanged
            }
            else if (cIdx === insertPos) {
                newColors[key] = color; // first half of split — keep color
                newColors["".concat(rIdx, "-").concat(cIdx + 1)] = color; // second half of split — inherit color
            }
            else {
                newColors["".concat(rIdx, "-").concat(cIdx + 1)] = color; // after new cut — shift right
            }
        }
    }
    return newColors;
}
var ArrayModel = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
    var data = _a.data;
    var isPreview = !!data.preview;
    var t = useTranslation().t;
    var zoomLevel = globalStore.useStoreState(function (s) { return s.playerStore; }).zoomLevel;
    var showExpression = (_b = data.showExpression) !== null && _b !== void 0 ? _b : true;
    var showProduct = (_c = data.showProduct) !== null && _c !== void 0 ? _c : true;
    // Independent mode flags - both can be active simultaneously
    var partModeEnabled = ((_d = data.partModeEnabled) !== null && _d !== void 0 ? _d : true) && !((_f = (_e = data.lock) === null || _e === void 0 ? void 0 : _e.editing) !== null && _f !== void 0 ? _f : false);
    var partModeEnabledRef = useRef(partModeEnabled);
    partModeEnabledRef.current = partModeEnabled;
    var _y = useState((_g = data.selectModeEnabled) !== null && _g !== void 0 ? _g : false), selectModeEnabled = _y[0], setSelectModeEnabled = _y[1];
    // which region is selected (grid or open)
    var _z = useState(null), selectedPartKey = _z[0], setSelectedPartKey = _z[1];
    var selectedPartKeyRef = useRef(selectedPartKey);
    selectedPartKeyRef.current = selectedPartKey;
    var selectModeEnabledRef = useRef(selectModeEnabled);
    selectModeEnabledRef.current = selectModeEnabled;
    //const { setManipulative, panningEnable } = useContext(DataContext);
    var _0 = useContext(DataContext), setManipulative = _0.setManipulative, manipulative = _0.manipulative, panningEnable = _0.panningEnable, selectedTool = _0.selectedTool, setSelectedTool = _0.setSelectedTool, setActiveManipulator = _0.setActiveManipulator, setManiplulatorComponentVisible = _0.setManiplulatorComponentVisible, setSelectedManipulativeId = _0.setSelectedManipulativeId, contextMenu = _0.contextMenu, setContextMenu = _0.setContextMenu;
    // Accessibility: one-time creation/duplicate announcements (Dice pattern)
    var duplicateLabel = useDuplicateAnnounce(data.id, t('text.arrayModels'));
    var creationLabel = useCreationAnnounce(data.id, t('text.arrayModels'));
    // Track if this array model is focused
    var _1 = useState(false), isFocused = _1[0], setIsFocused = _1[1];
    var isFocusedRef = useRef(false);
    // Accessibility: keyboard interaction refs (Dice pattern)
    var arrayInteractiveRef = useRef(null);
    var cutArrowRowRef = useRef(null);
    var cutArrowColRef = useRef(null);
    var cutMovePanelRef = useRef(null);
    var _2 = useState(''), announceText = _2[0], setAnnounceText = _2[1];
    var _3 = useState(''), assertiveText = _3[0], setAssertiveText = _3[1];
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    // Refs for expression/product focus management
    var expressionRef = useRef(null);
    var productRef = useRef(null);
    // Track hover state for arrow tooltips
    var _4 = useState(null), hoveredArrow = _4[0], setHoveredArrow = _4[1];
    var scale = (_j = (_h = data.groupScale) !== null && _h !== void 0 ? _h : data.scale) !== null && _j !== void 0 ? _j : 1;
    var mode = (_k = data.mode) !== null && _k !== void 0 ? _k : 'grid';
    // Keep a ref to latest data for use in event handlers
    var dataRef = useRef(data);
    useEffect(function () {
        dataRef.current = data;
    }, [data]);
    // Drag tracking ref (similar to NumberCharts)
    var dragInfoRef = useRef(null);
    // Track initial center for undo on position drag
    var initialCenterRef = useRef(null);
    // Tracks which region was touched on iOS so onUp can apply selection after drag detection
    var lastRegionTouchRef = useRef(0);
    var touchedRegionKeyRef = useRef(null);
    /* ================= TRACK FOCUSED STATE ================= */
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(data.id);
        var nowFocused = selectedTool.includes(data.id);
        prevSelectedToolRef.current = selectedTool;
        setIsFocused(nowFocused);
        isFocusedRef.current = nowFocused;
        if (!nowFocused) {
            setSelectedPartKey(null);
        }
        if (nowFocused && !wasFocused && !isPreview) {
            // Array just became selected — ensure the right panel opens regardless of
            // how the selection was triggered (tap on body, cut arrow, SelectFocusOverlay…)
            customEventHandler.dispatchEvent('openRightDrawer', {});
            // Auto-focus the array when newly selected (e.g. after popup Done, duplication).
            // Skip if context menu is open — it manages its own focus.
            // Skip if already focused (onAfterClick in SelectFocusOverlay may have handled it).
            if (arrayInteractiveRef.current && !contextMenu) {
                requestAnimationFrame(function () {
                    var _a;
                    if (document.activeElement !== arrayInteractiveRef.current) {
                        (_a = arrayInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                });
            }
        }
    }, [selectedTool, data.id]);
    /* ================= RIGHT PANEL EVENT LISTENER ================= */
    useEffect(function () {
        var onBottomPanelClick = function (eventData) {
            if (!isFocusedRef.current)
                return;
            switch (eventData.id) {
                case 'addRow':
                    addRow();
                    break;
                case 'removeRow':
                    removeRow();
                    break;
                case 'addColumn':
                    addColumn();
                    break;
                case 'removeColumn':
                    removeColumn();
                    break;
                case 'toggleExpression':
                    toggleExpression();
                    break;
                case 'toggleProduct':
                    toggleProduct();
                    break;
                case 'partMode':
                    onPartClick();
                    break;
                case 'selectMode':
                    onSelectClick();
                    break;
                case 'scaleMinus': {
                    var oldScale_1;
                    var newScale_1;
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.type === 'arrayModels' && item.data.id === data.id) {
                                oldScale_1 = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                                newScale_1 = Math.max(0.5, oldScale_1 - 0.1);
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_1 }) });
                            }
                            return item;
                        });
                    });
                    if (oldScale_1 !== undefined && newScale_1 !== undefined) {
                        saveState({
                            type: data.id,
                            id: data.id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(oldScale_1),
                            new: JSON.stringify(newScale_1),
                        });
                    }
                    break;
                }
                case 'scalePlus': {
                    var oldScale_2;
                    var newScale_2;
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.type === 'arrayModels' && item.data.id === data.id) {
                                oldScale_2 = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                                newScale_2 = oldScale_2 + 0.1;
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_2 }) });
                            }
                            return item;
                        });
                    });
                    if (oldScale_2 !== undefined && newScale_2 !== undefined) {
                        saveState({
                            type: data.id,
                            id: data.id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(oldScale_2),
                            new: JSON.stringify(newScale_2),
                        });
                    }
                    break;
                }
                default:
                    break;
            }
        };
        customEventHandler.addEventListener('bottomPanel', onBottomPanelClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onBottomPanelClick);
        };
    }, [data.id]);
    /* ================= APPLY COLOR TO SELECTED PART ================= */
    // Listen to the colourChange custom event (fired by ColorPicker on every pick,
    // even if the same color is picked again). This solves two problems vs. a
    // useEffect on the `color` context value:
    //   1. Picking the same color on a NEW section now works (event always fires).
    //   2. Selecting a section never auto-colors it (event only fires from the picker).
    useEffect(function () {
        var onColourChange = function (eventData) {
            var _a;
            var _b;
            if (!isFocusedRef.current)
                return;
            if (!selectModeEnabledRef.current)
                return;
            if (!selectedPartKeyRef.current)
                return;
            var sectionKey = selectedPartKeyRef.current;
            var newColor = ColorEnum[eventData.colorKey];
            var oldData = structuredClone(dataRef.current);
            var newData = __assign(__assign({}, oldData), { partColors: __assign(__assign({}, ((_b = oldData.partColors) !== null && _b !== void 0 ? _b : {})), (_a = {}, _a[sectionKey] = newColor, _a)) });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'arrayModels'
                        ? __assign(__assign({}, item), { data: newData }) : item;
                });
            });
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData),
            });
            // Announce color change for screen readers (WCAG 4.1.3)
            announce(t('text.arrayModelSectionColored', { color: eventData.colorKey.toLowerCase() }));
        };
        customEventHandler.addEventListener('colourChange', onColourChange);
        return function () {
            customEventHandler.removeEventListener('colourChange', onColourChange);
        };
    }, [data.id, setManipulative]);
    /* ================= SYNC SELECTED PART KEY (for ColorPicker disable logic) ================= */
    useEffect(function () {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'arrayModels'
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { selectedPartKey: selectedPartKey }) }) : item;
            });
        });
    }, [selectedPartKey, data.id, setManipulative]);
    /* ================= PERSIST MODE CHANGES ================= */
    useEffect(function () {
        if (data.selectModeEnabled !== selectModeEnabled) {
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'arrayModels'
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { selectModeEnabled: selectModeEnabled }) }) : item;
                });
            });
        }
    }, [
        selectModeEnabled,
        data.id,
        data.selectModeEnabled,
        setManipulative,
    ]);
    var cutRows = __spreadArray([], ((_l = data.cutRows) !== null && _l !== void 0 ? _l : []), true).sort(function (a, b) { return a - b; });
    var cutColumns = __spreadArray([], ((_m = data.cutColumns) !== null && _m !== void 0 ? _m : []), true).sort(function (a, b) { return a - b; });
    /* ---------------- SIZE ---------------- */
    var workspaceCellSize = (_o = data.cellSize) !== null && _o !== void 0 ? _o : DEFAULT_WORKSPACE_CELL_SIZE;
    var cellSize = isPreview
        ? Math.floor(Math.min(PREVIEW_SIZE / data.columns, PREVIEW_SIZE / data.rows))
        : workspaceCellSize;
    var width = mode === 'open'
        ? data.columns * (isPreview ? cellSize : workspaceCellSize)
        : data.columns * cellSize;
    var height = mode === 'open'
        ? data.rows * (isPreview ? cellSize : workspaceCellSize)
        : data.rows * cellSize;
    /* ================= GRID DRAG ================= */
    var gridDragRef = useRef(null);
    // Holds the cleanup fn for window listeners so they're removed on unmount
    var gridDragCleanupRef = useRef(null);
    useEffect(function () {
        return function () {
            var _a;
            (_a = gridDragCleanupRef.current) === null || _a === void 0 ? void 0 : _a.call(gridDragCleanupRef);
        };
    }, []);
    var _5 = useMultiSelectDragSource(data.id, selectedTool), startMultiDrag = _5.startMultiDrag, moveMultiDrag = _5.moveMultiDrag, endMultiDrag = _5.endMultiDrag;
    var onGridMouseDown = function (e) {
        if (isPreview || panningEnable)
            return;
        if (cutDragRef.current)
            return;
        // Guard against multi-touch: ignore if drag already active
        if (gridDragRef.current)
            return;
        var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        dragInfoRef.current = { startX: x, startY: y, hasMoved: false };
        initialCenterRef.current = __assign({}, data.center);
        gridDragRef.current = {
            x: x - data.center.x * zoomLevel,
            y: y - data.center.y * zoomLevel,
        };
        startMultiDrag();
        var onMove = function (ev) {
            var _a;
            if (!gridDragRef.current)
                return;
            if ((_a = dataRef.current.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                return;
            var ex = 'clientX' in ev ? ev.clientX : ev.touches[0].clientX;
            var ey = 'clientY' in ev ? ev.clientY : ev.touches[0].clientY;
            if (dragInfoRef.current && !dragInfoRef.current.hasMoved) {
                var dx = ex - dragInfoRef.current.startX;
                var dy = ey - dragInfoRef.current.startY;
                if (Math.sqrt(dx * dx + dy * dy) > 5) {
                    dragInfoRef.current.hasMoved = true;
                }
                else {
                    return;
                }
            }
            // Capture offsets synchronously — gridDragRef.current may be nulled
            // by onUp before React runs the functional state-update callback
            var offsetX = gridDragRef.current.x;
            var offsetY = gridDragRef.current.y;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'arrayModels'
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: {
                                    x: (ex - offsetX) / zoomLevel,
                                    y: (ey - offsetY) / zoomLevel,
                                } }) }) : item;
                });
            });
            if (initialCenterRef.current) {
                moveMultiDrag((ex - offsetX) / zoomLevel - initialCenterRef.current.x, (ey - offsetY) / zoomLevel - initialCenterRef.current.y);
            }
        };
        var onUp = function (e) {
            var _a;
            if (dragInfoRef.current && !dragInfoRef.current.hasMoved) {
                // Route through `selectTool` so Shift / Ctrl / Cmd toggle into existing
                // multi-selection. The native mouseup carries modifier flags; for
                // touchend (no flags) the helper falls back to single-select.
                setSelectedTool(selectTool(e, data.id, selectedTool, false));
                // Any tap on the array body (including parts) should open the right panel
                customEventHandler.dispatchEvent('openRightDrawer', {});
                // On iPad: apply region selection that was stored on touchstart
                if (touchedRegionKeyRef.current !== null && selectModeEnabled) {
                    var k_1 = touchedRegionKeyRef.current;
                    setSelectedPartKey(function (prev) { return (prev === k_1 ? null : k_1); });
                }
            }
            else if (((_a = dragInfoRef.current) === null || _a === void 0 ? void 0 : _a.hasMoved) && initialCenterRef.current) {
                // Suppress any onClick that fires on a region after a drag ends over it
                lastRegionTouchRef.current = Date.now();
                var oldCenter = initialCenterRef.current;
                var newCenter = dataRef.current.center;
                var entry = {
                    type: data.id,
                    id: data.id.split('_')[1],
                    propertyName: 'center',
                    old: JSON.stringify(oldCenter),
                    new: JSON.stringify(newCenter),
                };
                if (!registerMultiSelectSaveEntries(data.id, [entry])) {
                    saveState(entry);
                }
                setSelectedTool(selectTool(e, data.id, selectedTool, false));
            }
            gridDragRef.current = null;
            dragInfoRef.current = null;
            initialCenterRef.current = null;
            touchedRegionKeyRef.current = null;
            cleanup();
            gridDragCleanupRef.current = null;
            endMultiDrag();
        };
        var cleanup = function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
        gridDragCleanupRef.current = cleanup;
    };
    /* ================= CUT DRAG ================= */
    var _6 = useState(null), cutPreview = _6[0], setCutPreview = _6[1];
    // Ref mirror so onUp always reads the latest cutPreview without re-registering listeners
    var cutPreviewRef = useRef(cutPreview);
    cutPreviewRef.current = cutPreview;
    var cutDragRef = useRef(null);
    // Track which existing cut is being re-dragged (to hide it during drag)
    var _7 = useState(null), draggingExistingCut = _7[0], setDraggingExistingCut = _7[1];
    // ── Keyboard cut placement ("pick and drop") ──
    // When active, the user is positioning a cut line via arrow keys before committing.
    var _8 = useState(null), kbCutMode = _8[0], setKbCutMode = _8[1];
    var kbCutModeRef = useRef(kbCutMode);
    kbCutModeRef.current = kbCutMode;
    // Show the orange preview line while in keyboard cut mode
    useEffect(function () {
        if (kbCutMode) {
            var pixelValue = mode === 'grid'
                ? kbCutMode.position * cellSize
                : kbCutMode.position;
            setCutPreview({ type: kbCutMode.type, value: pixelValue });
        }
    }, [kbCutMode, mode, cellSize]);
    // When kbCutMode first activates (null → non-null), select the array model and open
    // the right panel on ALL devices — the user is interacting with this array regardless
    // of which manipulative was selected before. On touch, also move VoiceOver focus to
    // the cutMovePanel button so VO doesn't drift to the toolbar.
    var kbCutModeWasNullRef = useRef(true);
    useEffect(function () {
        if (kbCutMode && kbCutModeWasNullRef.current) {
            kbCutModeWasNullRef.current = false;
            // Select this array and open the right panel for all devices
            if (!isFocusedRef.current) {
                setSelectedTool([data.id]);
            }
            customEventHandler.dispatchEvent('openRightDrawer', {});
            requestAnimationFrame(function () {
                var _a;
                var firstEnabled = (_a = cutMovePanelRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not(:disabled)');
                firstEnabled === null || firstEnabled === void 0 ? void 0 : firstEnabled.focus();
            });
        }
        else if (!kbCutMode) {
            kbCutModeWasNullRef.current = true;
        }
    }, [kbCutMode, data.id, setSelectedTool]);
    // If the focused panel button becomes disabled (cut reached a boundary),
    // move focus to the next enabled button so it never rests on a disabled element.
    useEffect(function () {
        if (!kbCutMode)
            return;
        var panel = cutMovePanelRef.current;
        if (!panel)
            return;
        var focused = document.activeElement;
        if ((focused === null || focused === void 0 ? void 0 : focused.disabled) && panel.contains(focused)) {
            var firstEnabled = panel.querySelector('button:not(:disabled)');
            firstEnabled === null || firstEnabled === void 0 ? void 0 : firstEnabled.focus();
        }
    }, [kbCutMode, data.cutRows, data.cutColumns]);
    /** Commit a keyboard-placed cut line */
    var commitKbCut = useCallback(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (!kbCutModeRef.current)
            return;
        var _s = kbCutModeRef.current, cutType = _s.type, position = _s.position;
        var oldData = structuredClone(dataRef.current);
        if (mode === 'grid') {
            var limit = cutType === 'row' ? oldData.rows : oldData.columns;
            if (position < 1 || position >= limit) {
                // Invalid position — cancel
                setKbCutMode(null);
                setCutPreview(null);
                announce(t('text.arrayModelCutCancelled'));
                return;
            }
            var maxCuts = Math.min(limit - 1, 4);
            var existingCuts = cutType === 'row' ? ((_a = oldData.cutRows) !== null && _a !== void 0 ? _a : []) : ((_b = oldData.cutColumns) !== null && _b !== void 0 ? _b : []);
            if (existingCuts.length >= maxCuts) {
                setKbCutMode(null);
                setCutPreview(null);
                announce(t('text.arrayModelCutMaxReached'));
                return;
            }
            if (existingCuts.includes(position)) {
                // Already a cut here — cancel
                setKbCutMode(null);
                setCutPreview(null);
                announce(t('text.arrayModelCutAlreadyExists'));
                return;
            }
            var sortedExisting = __spreadArray([], existingCuts, true).sort(function (a, b) { return a - b; });
            var insertPos = sortedExisting.filter(function (c) { return c < position; }).length;
            var newData_1 = __assign(__assign({}, oldData), { cutRows: cutType === 'row'
                    ? Array.from(new Set(__spreadArray(__spreadArray([], existingCuts, true), [position], false)))
                    : ((_c = oldData.cutRows) !== null && _c !== void 0 ? _c : []), cutColumns: cutType === 'col'
                    ? Array.from(new Set(__spreadArray(__spreadArray([], existingCuts, true), [position], false)))
                    : ((_d = oldData.cutColumns) !== null && _d !== void 0 ? _d : []), partColors: remapColorsAfterCutAddition(oldData.partColors, cutType, insertPos) });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'arrayModels'
                        ? __assign(__assign({}, item), { data: newData_1 }) : item;
                });
            });
            playAudio('scissor');
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData_1),
            });
            announce(cutType === 'row'
                ? t('text.arrayModelRowDivided', { position: position })
                : t('text.arrayModelColumnDivided', { position: position }));
        }
        else {
            // Open mode — position is in pixels, convert to percentage
            var dimension = cutType === 'row' ? height : width;
            var percent_1 = position / dimension;
            var minEdgePx = 45;
            var minPercent = minEdgePx / dimension;
            var maxPercent = 1 - minEdgePx / dimension;
            if (percent_1 <= minPercent || percent_1 >= maxPercent) {
                setKbCutMode(null);
                setCutPreview(null);
                announce(t('text.arrayModelCutCancelled'));
                return;
            }
            var existingCuts = cutType === 'row' ? ((_f = (_e = oldData.openCuts) === null || _e === void 0 ? void 0 : _e.rows) !== null && _f !== void 0 ? _f : []) : ((_h = (_g = oldData.openCuts) === null || _g === void 0 ? void 0 : _g.cols) !== null && _h !== void 0 ? _h : []);
            if (existingCuts.length >= 4) {
                setKbCutMode(null);
                setCutPreview(null);
                announce(t('text.arrayModelCutMaxReached'));
                return;
            }
            var sortedExistingOpen = __spreadArray([], existingCuts, true).sort(function (a, b) { return a - b; });
            var insertPosOpen = sortedExistingOpen.filter(function (c) { return c < percent_1; }).length;
            var newData_2 = __assign(__assign({}, oldData), { openCuts: {
                    rows: cutType === 'row'
                        ? Array.from(new Set(__spreadArray(__spreadArray([], ((_k = (_j = oldData.openCuts) === null || _j === void 0 ? void 0 : _j.rows) !== null && _k !== void 0 ? _k : []), true), [percent_1], false)))
                        : ((_m = (_l = oldData.openCuts) === null || _l === void 0 ? void 0 : _l.rows) !== null && _m !== void 0 ? _m : []),
                    cols: cutType === 'col'
                        ? Array.from(new Set(__spreadArray(__spreadArray([], ((_p = (_o = oldData.openCuts) === null || _o === void 0 ? void 0 : _o.cols) !== null && _p !== void 0 ? _p : []), true), [percent_1], false)))
                        : ((_r = (_q = oldData.openCuts) === null || _q === void 0 ? void 0 : _q.cols) !== null && _r !== void 0 ? _r : []),
                }, partColors: remapColorsAfterCutAddition(oldData.partColors, cutType, insertPosOpen) });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'arrayModels'
                        ? __assign(__assign({}, item), { data: newData_2 }) : item;
                });
            });
            playAudio('scissor');
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData_2),
            });
            announce(cutType === 'row'
                ? t('text.arrayModelRowDivided', { position: Math.round(percent_1 * 100) + '%' })
                : t('text.arrayModelColumnDivided', { position: Math.round(percent_1 * 100) + '%' }));
        }
        // Select this array and open the right panel after confirming the cut
        setSelectedTool([data.id]);
        customEventHandler.dispatchEvent('openRightDrawer', {});
        setKbCutMode(null);
        setCutPreview(null);
        // Return focus to the create-part arrow so the user can place another cut.
        requestAnimationFrame(function () {
            var _a;
            var target = cutType === 'row' ? cutArrowRowRef.current : cutArrowColRef.current;
            if (target && target.tabIndex !== -1) {
                target.focus();
            }
            else {
                (_a = arrayInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        });
    }, [mode, cellSize, height, width, data.id, setManipulative, setSelectedTool, t]);
    /** Handle keyboard events during cut placement mode */
    var handleKbCutKeyDown = useCallback(function (e, cutType) {
        if (!kbCutModeRef.current)
            return;
        var isRow = cutType === 'row';
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setKbCutMode(null);
            setCutPreview(null);
            announce(t('text.arrayModelCutCancelled'));
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            commitKbCut();
            return;
        }
        // Arrow key to move the cut position
        var movingKey = isRow
            ? (e.key === 'ArrowUp' || e.key === 'ArrowDown')
            : (e.key === 'ArrowLeft' || e.key === 'ArrowRight');
        if (!movingKey)
            return;
        e.preventDefault();
        e.stopPropagation();
        var delta = (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? 1 : -1;
        setKbCutMode(function (prev) {
            if (!prev)
                return prev;
            if (mode === 'grid') {
                var limit = isRow ? dataRef.current.rows : dataRef.current.columns;
                var next = Math.max(1, Math.min(limit - 1, prev.position + delta));
                announce(isRow
                    ? t('text.arrayModelCutPreviewRow', { position: next, total: limit })
                    : t('text.arrayModelCutPreviewCol', { position: next, total: limit }));
                return __assign(__assign({}, prev), { position: next });
            }
            else {
                // Open mode — move by ~10% of dimension
                var dimension = isRow ? height : width;
                var step = dimension * 0.1;
                var next = Math.max(0, Math.min(dimension, prev.position + delta * step));
                var pct = Math.round((next / dimension) * 100);
                announce(isRow
                    ? t('text.arrayModelCutPreviewRow', { position: pct + '%', total: '100%' })
                    : t('text.arrayModelCutPreviewCol', { position: pct + '%', total: '100%' }));
                return __assign(__assign({}, prev), { position: next });
            }
        });
    }, [mode, height, width, commitKbCut, t]);
    /** Programmatic equivalent of pressing an arrow key in kbCutMode — used by touch buttons */
    var moveKbCut = useCallback(function (delta) {
        var _a;
        var mode_ = (_a = dataRef.current.mode) !== null && _a !== void 0 ? _a : 'grid';
        var cur = kbCutModeRef.current;
        if (!cur)
            return;
        var isRow = cur.type === 'row';
        setKbCutMode(function (prev) {
            if (!prev)
                return prev;
            if (mode_ === 'grid') {
                var limit = isRow ? dataRef.current.rows : dataRef.current.columns;
                var next = Math.max(1, Math.min(limit - 1, prev.position + delta));
                announce(isRow
                    ? t('text.arrayModelCutPreviewRow', { position: next, total: limit })
                    : t('text.arrayModelCutPreviewCol', { position: next, total: limit }));
                return __assign(__assign({}, prev), { position: next });
            }
            else {
                var dimension = isRow ? height : width;
                var step = dimension * 0.1;
                var next = Math.max(0, Math.min(dimension, prev.position + delta * step));
                var pct = Math.round((next / dimension) * 100);
                announce(isRow
                    ? t('text.arrayModelCutPreviewRow', { position: pct + '%', total: '100%' })
                    : t('text.arrayModelCutPreviewCol', { position: pct + '%', total: '100%' }));
                return __assign(__assign({}, prev), { position: next });
            }
        });
    }, [height, width, t]);
    /** Delete an existing cut line by value */
    var deleteExistingCut = useCallback(function (cutType, cutValue) {
        var _a, _b, _c, _d, _e, _f;
        var oldData = structuredClone(dataRef.current);
        var newData;
        var cutIdx = -1;
        if (mode === 'grid') {
            var existingRows = __spreadArray([], ((_a = oldData.cutRows) !== null && _a !== void 0 ? _a : []), true).sort(function (a, b) { return a - b; });
            var existingCols = __spreadArray([], ((_b = oldData.cutColumns) !== null && _b !== void 0 ? _b : []), true).sort(function (a, b) { return a - b; });
            if (cutType === 'row') {
                cutIdx = existingRows.indexOf(cutValue);
                if (cutIdx !== -1)
                    existingRows.splice(cutIdx, 1);
            }
            else {
                cutIdx = existingCols.indexOf(cutValue);
                if (cutIdx !== -1)
                    existingCols.splice(cutIdx, 1);
            }
            newData = __assign(__assign({}, oldData), { cutRows: existingRows, cutColumns: existingCols, partColors: remapColorsAfterCutRemoval(oldData.partColors, cutType, cutIdx) });
        }
        else {
            // Open mode — cutValue is a percentage
            var existingRows = __spreadArray([], ((_d = (_c = oldData.openCuts) === null || _c === void 0 ? void 0 : _c.rows) !== null && _d !== void 0 ? _d : []), true).sort(function (a, b) { return a - b; });
            var existingCols = __spreadArray([], ((_f = (_e = oldData.openCuts) === null || _e === void 0 ? void 0 : _e.cols) !== null && _f !== void 0 ? _f : []), true).sort(function (a, b) { return a - b; });
            if (cutType === 'row') {
                cutIdx = existingRows.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                if (cutIdx !== -1)
                    existingRows.splice(cutIdx, 1);
            }
            else {
                cutIdx = existingCols.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                if (cutIdx !== -1)
                    existingCols.splice(cutIdx, 1);
            }
            newData = __assign(__assign({}, oldData), { openCuts: { rows: existingRows, cols: existingCols }, partColors: remapColorsAfterCutRemoval(oldData.partColors, cutType, cutIdx) });
        }
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'arrayModels'
                    ? __assign(__assign({}, item), { data: newData }) : item;
            });
        });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        announce(t('text.arrayModelCutRemoved'));
    }, [mode, data.id, setManipulative, t]);
    /** Keyboard handler for existing cut arrows — reposition or delete */
    var handleExistingCutKeyDown = useCallback(function (e, cutType, cutValue) {
        var _a, _b, _c, _d, _e, _f;
        if (!partModeEnabled)
            return;
        // Delete / Backspace — remove the cut line
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            e.stopPropagation();
            deleteExistingCut(cutType, cutValue);
            // Return focus to the main wrapper after deletion
            requestAnimationFrame(function () { var _a; return (_a = arrayInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
            return;
        }
        // Enter / Space — pick up for repositioning (reuses kbCutMode)
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            // Remove the existing cut first, then enter placement mode at its position
            var oldData = structuredClone(dataRef.current);
            var newData_3;
            if (mode === 'grid') {
                var existingRows = __spreadArray([], ((_a = oldData.cutRows) !== null && _a !== void 0 ? _a : []), true);
                var existingCols = __spreadArray([], ((_b = oldData.cutColumns) !== null && _b !== void 0 ? _b : []), true);
                if (cutType === 'row') {
                    var idx = existingRows.indexOf(cutValue);
                    if (idx !== -1)
                        existingRows.splice(idx, 1);
                }
                else {
                    var idx = existingCols.indexOf(cutValue);
                    if (idx !== -1)
                        existingCols.splice(idx, 1);
                }
                newData_3 = __assign(__assign({}, oldData), { cutRows: existingRows, cutColumns: existingCols });
                // Enter placement mode at the current position
                setKbCutMode({ type: cutType, position: cutValue });
            }
            else {
                var existingRows = __spreadArray([], ((_d = (_c = oldData.openCuts) === null || _c === void 0 ? void 0 : _c.rows) !== null && _d !== void 0 ? _d : []), true);
                var existingCols = __spreadArray([], ((_f = (_e = oldData.openCuts) === null || _e === void 0 ? void 0 : _e.cols) !== null && _f !== void 0 ? _f : []), true);
                if (cutType === 'row') {
                    var idx = existingRows.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                    if (idx !== -1)
                        existingRows.splice(idx, 1);
                }
                else {
                    var idx = existingCols.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                    if (idx !== -1)
                        existingCols.splice(idx, 1);
                }
                newData_3 = __assign(__assign({}, oldData), { openCuts: { rows: existingRows, cols: existingCols } });
                // Enter placement mode — convert percentage to pixels for open mode
                var pixelPos = cutType === 'row' ? cutValue * height : cutValue * width;
                setKbCutMode({ type: cutType, position: pixelPos });
            }
            // Apply the removal
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'arrayModels'
                        ? __assign(__assign({}, item), { data: newData_3 }) : item;
                });
            });
            // Save state for the removal (the re-placement will save separately via commitKbCut)
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData_3),
            });
            var posLabel = mode === 'grid' ? String(cutValue) : Math.round(cutValue * 100) + '%';
            announce(t('text.arrayModelCutPickedUp', { type: cutType === 'row' ? t('text.rows') : t('text.columns'), position: posLabel }));
            // Move focus to the edge cut arrow so arrow keys continue reaching the placement handler
            // (the existing cut arrow unmounts when the cut is removed from data).
            requestAnimationFrame(function () {
                var _a;
                var targetRef = cutType === 'row' ? cutArrowRowRef : cutArrowColRef;
                (_a = targetRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            });
            return;
        }
        // If in placement mode (after Enter), delegate to placement handler
        if (kbCutModeRef.current) {
            handleKbCutKeyDown(e, cutType);
        }
    }, [partModeEnabled, mode, height, width, data.id, setManipulative, deleteExistingCut, handleKbCutKeyDown, t]);
    // Resize drag tracking for open mode
    var resizeDragRef = useRef(null);
    var onCutStart = function (e, type) {
        if (!partModeEnabled)
            return; // Only allow cut when part mode is enabled
        e.stopPropagation();
        e.preventDefault();
        var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        cutDragRef.current = { type: type, startX: x, startY: y };
        setCutPreview(null);
    };
    var onExistingCutStart = function (e, type, cutValue) {
        var _a, _b, _c, _d;
        if (!partModeEnabled)
            return;
        e.stopPropagation();
        e.preventDefault();
        var isTouchEv = 'touches' in e;
        var x = !isTouchEv ? e.clientX : (_b = (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.clientX) !== null && _b !== void 0 ? _b : 0;
        var y = !isTouchEv ? e.clientY : (_d = (_c = e.touches[0]) === null || _c === void 0 ? void 0 : _c.clientY) !== null && _d !== void 0 ? _d : 0;
        // Offset startX/startY so localX/localY equals current pixel position at drag start
        // For grid mode: cutValue is an index, convert to pixels
        // For open mode: cutValue is a percentage, convert to pixels
        var currentPixelPos = mode === 'grid' ? cutValue * cellSize : type === 'row' ? cutValue * height : cutValue * width;
        cutDragRef.current = {
            type: type,
            startX: type === 'col' ? x - currentPixelPos * scale * zoomLevel : x,
            startY: type === 'row' ? y - currentPixelPos * scale * zoomLevel : y,
            existingCutValue: cutValue,
        };
        setCutPreview({ type: type, value: currentPixelPos });
        setDraggingExistingCut({ type: type, value: cutValue });
    };
    // Touch-only version: skips e.preventDefault() so iOS tracks the touch correctly
    // for drag on the first attempt, and so the synthetic click fires for kbCutMode
    // (VoiceOver double-tap and regular tap both rely on click). Scroll prevention is
    // achieved via touch-action:none on the indicator element instead.
    var onExistingCutTouchStart = function (e, type, cutValue) {
        if (!partModeEnabled)
            return;
        e.stopPropagation(); // prevent array from being dragged
        // no e.preventDefault() — keeps click alive for kbCutMode and fixes iOS drag tracking
        var touch = e.touches[0];
        if (!touch)
            return; // guard for VoiceOver synthetic events
        var x = touch.clientX;
        var y = touch.clientY;
        var currentPixelPos = mode === 'grid' ? cutValue * cellSize : type === 'row' ? cutValue * height : cutValue * width;
        cutDragRef.current = {
            type: type,
            startX: type === 'col' ? x - currentPixelPos * scale * zoomLevel : x,
            startY: type === 'row' ? y - currentPixelPos * scale * zoomLevel : y,
            existingCutValue: cutValue,
        };
        setCutPreview({ type: type, value: currentPixelPos });
        setDraggingExistingCut({ type: type, value: cutValue });
        // Select the array on first touch so iOS considers it active for drag
        if (!isFocusedRef.current) {
            setSelectedTool([data.id]);
        }
    };
    useEffect(function () {
        var onMove = function (e) {
            if (!cutDragRef.current)
                return;
            cutDragRef.current.hasMoved = true;
            var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var _a = cutDragRef.current, type = _a.type, startX = _a.startX, startY = _a.startY;
            var localX = (x - startX) / (scale * zoomLevel);
            var localY = (y - startY) / (scale * zoomLevel);
            if (mode === 'grid') {
                // Allow dragging to full extent of grid for smooth preview
                if (type === 'row') {
                    var maxY = data.rows * cellSize;
                    setCutPreview({ type: type, value: Math.max(0, Math.min(maxY, localY)) });
                }
                if (type === 'col') {
                    var maxX = data.columns * cellSize;
                    setCutPreview({ type: type, value: Math.max(0, Math.min(maxX, localX)) });
                }
            }
            else {
                // Open mode - clamp to grid boundaries (0 to height/width)
                if (type === 'row') {
                    setCutPreview({ type: type, value: Math.max(0, Math.min(height, localY)) });
                }
                else {
                    setCutPreview({ type: type, value: Math.max(0, Math.min(width, localX)) });
                }
            }
        };
        var onUp = function () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4;
            var currentCutPreview = cutPreviewRef.current;
            if (!cutDragRef.current || !currentCutPreview) {
                // Tap (no drag movement) on a new-cut arrow — enter kbCutMode for the panel flow
                var enteredKbCutMode = false;
                if (cutDragRef.current && !currentCutPreview && cutDragRef.current.existingCutValue == null) {
                    var tapType = cutDragRef.current.type;
                    var d = dataRef.current;
                    var existingCuts = tapType === 'row' ? ((_a = d.cutRows) !== null && _a !== void 0 ? _a : []) : ((_b = d.cutColumns) !== null && _b !== void 0 ? _b : []);
                    var limit = tapType === 'row' ? d.rows : d.columns;
                    if (existingCuts.length < Math.min(limit - 1, 4)) {
                        var startPos = mode === 'grid' ? 1 : (tapType === 'row' ? height : width) / 2;
                        setKbCutMode({ type: tapType, position: startPos });
                        // Set preview immediately so the orange line is visible as soon as the panel opens
                        var pixelValue = mode === 'grid' ? startPos * cellSize : startPos;
                        setCutPreview({ type: tapType, value: pixelValue });
                        enteredKbCutMode = true;
                        var posLabel = mode === 'grid' ? '1' : '50%';
                        announce(t('text.arrayModelCutStarted', {
                            type: tapType === 'row' ? t('text.rows') : t('text.columns'),
                            position: posLabel,
                        }));
                    }
                }
                // Clear re-drag state even if drag was cancelled
                if (((_c = cutDragRef.current) === null || _c === void 0 ? void 0 : _c.existingCutValue) != null) {
                    setDraggingExistingCut(null);
                }
                cutDragRef.current = null;
                if (!enteredKbCutMode) {
                    setCutPreview(null);
                }
                return;
            }
            // Compute old and new data inline (don't rely on setTimeout + dataRef)
            var oldData = structuredClone(dataRef.current);
            var newData;
            var existingCutValue = cutDragRef.current.existingCutValue;
            if (mode === 'grid') {
                // Snap raw pixel position to nearest row/column index
                var snapped_1 = Math.round(currentCutPreview.value / cellSize);
                var isRow = currentCutPreview.type === 'row';
                var limit = isRow ? oldData.rows : oldData.columns;
                if (existingCutValue != null) {
                    // ── Re-dragging an existing cut ──
                    var existingRows = __spreadArray([], ((_d = oldData.cutRows) !== null && _d !== void 0 ? _d : []), true);
                    var existingCols = __spreadArray([], ((_e = oldData.cutColumns) !== null && _e !== void 0 ? _e : []), true);
                    // Remove the original cut
                    if (isRow) {
                        var idx = existingRows.indexOf(existingCutValue);
                        if (idx !== -1)
                            existingRows.splice(idx, 1);
                    }
                    else {
                        var idx = existingCols.indexOf(existingCutValue);
                        if (idx !== -1)
                            existingCols.splice(idx, 1);
                    }
                    // Add at new position if valid (snapped >= 1 && < limit) and not duplicate
                    var wasDeleted = snapped_1 < 1 || snapped_1 >= limit;
                    var dragCutIdx = -1;
                    if (wasDeleted) {
                        // The cut was dragged off — find its sorted index before removal for color remap
                        var sortedBefore = isRow
                            ? __spreadArray([], ((_f = oldData.cutRows) !== null && _f !== void 0 ? _f : []), true).sort(function (a, b) { return a - b; })
                            : __spreadArray([], ((_g = oldData.cutColumns) !== null && _g !== void 0 ? _g : []), true).sort(function (a, b) { return a - b; });
                        dragCutIdx = sortedBefore.indexOf(existingCutValue);
                    }
                    if (snapped_1 >= 1 && snapped_1 < limit) {
                        if (isRow && !existingRows.includes(snapped_1)) {
                            existingRows.push(snapped_1);
                        }
                        else if (!isRow && !existingCols.includes(snapped_1)) {
                            existingCols.push(snapped_1);
                        }
                    }
                    newData = __assign(__assign({}, oldData), { cutRows: existingRows, cutColumns: existingCols, partColors: wasDeleted
                            ? remapColorsAfterCutRemoval(oldData.partColors, isRow ? 'row' : 'col', dragCutIdx)
                            : oldData.partColors });
                    setDraggingExistingCut(null);
                    // On touch: a tap (no drag) resets state and lets the onClick handler
                    // enter kbCutMode — this also covers VoiceOver double-tap which fires click.
                    if (IS_TOUCH_DEVICE && !((_h = cutDragRef.current) === null || _h === void 0 ? void 0 : _h.hasMoved)) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                    // Skip save if nothing actually changed
                    var unchanged = JSON.stringify((_j = oldData.cutRows) !== null && _j !== void 0 ? _j : []) === JSON.stringify(existingRows) &&
                        JSON.stringify((_k = oldData.cutColumns) !== null && _k !== void 0 ? _k : []) === JSON.stringify(existingCols);
                    if (unchanged) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                }
                else {
                    // ── New cut ──
                    // Validate: must be between 1 and limit-1 (cannot be at edges)
                    if (snapped_1 < 1 || snapped_1 >= limit) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                    var maxRowCuts = Math.min(oldData.rows - 1, 4);
                    var maxColCuts = Math.min(oldData.columns - 1, 4);
                    var existingRows = (_l = oldData.cutRows) !== null && _l !== void 0 ? _l : [];
                    var existingCols = (_m = oldData.cutColumns) !== null && _m !== void 0 ? _m : [];
                    var existingAlongAxis = isRow ? existingRows : existingCols;
                    var sortedAlongAxis = __spreadArray([], existingAlongAxis, true).sort(function (a, b) { return a - b; });
                    var insertPosGrid = sortedAlongAxis.filter(function (c) { return c < snapped_1; }).length;
                    newData = __assign(__assign({}, oldData), { cutRows: currentCutPreview.type === 'row'
                            ? existingRows.length < maxRowCuts
                                ? Array.from(new Set(__spreadArray(__spreadArray([], existingRows, true), [snapped_1], false)))
                                : existingRows
                            : existingRows, cutColumns: currentCutPreview.type === 'col'
                            ? existingCols.length < maxColCuts
                                ? Array.from(new Set(__spreadArray(__spreadArray([], existingCols, true), [snapped_1], false)))
                                : existingCols
                            : existingCols, partColors: remapColorsAfterCutAddition(oldData.partColors, currentCutPreview.type, insertPosGrid) });
                }
            }
            else {
                // Open mode
                var percent_2 = currentCutPreview.type === 'row'
                    ? currentCutPreview.value / height
                    : currentCutPreview.value / width;
                // Minimum distance from edges (in pixels) to avoid arrow overlap
                var minEdgePx = 45;
                var isRow = currentCutPreview.type === 'row';
                var dimension = isRow ? height : width;
                var minPercent = minEdgePx / dimension;
                var maxPercent = 1 - minEdgePx / dimension;
                if (existingCutValue != null) {
                    // ── Re-dragging an existing cut in open mode ──
                    var existingRows = __spreadArray([], ((_p = (_o = oldData.openCuts) === null || _o === void 0 ? void 0 : _o.rows) !== null && _p !== void 0 ? _p : []), true);
                    var existingCols = __spreadArray([], ((_r = (_q = oldData.openCuts) === null || _q === void 0 ? void 0 : _q.cols) !== null && _r !== void 0 ? _r : []), true);
                    // Remove the original cut (existingCutValue is a percentage)
                    if (isRow) {
                        var idx = existingRows.findIndex(function (p) { return Math.abs(p - existingCutValue) < 0.001; });
                        if (idx !== -1)
                            existingRows.splice(idx, 1);
                    }
                    else {
                        var idx = existingCols.findIndex(function (p) { return Math.abs(p - existingCutValue) < 0.001; });
                        if (idx !== -1)
                            existingCols.splice(idx, 1);
                    }
                    // Add at new position if valid (away from edges) and not duplicate
                    var openWasDeleted = percent_2 <= minPercent || percent_2 >= maxPercent;
                    var openDragCutIdx = -1;
                    if (openWasDeleted) {
                        var sortedBefore = isRow
                            ? __spreadArray([], ((_t = (_s = oldData.openCuts) === null || _s === void 0 ? void 0 : _s.rows) !== null && _t !== void 0 ? _t : []), true).sort(function (a, b) { return a - b; })
                            : __spreadArray([], ((_v = (_u = oldData.openCuts) === null || _u === void 0 ? void 0 : _u.cols) !== null && _v !== void 0 ? _v : []), true).sort(function (a, b) { return a - b; });
                        openDragCutIdx = sortedBefore.findIndex(function (p) { return Math.abs(p - existingCutValue) < 0.001; });
                    }
                    if (percent_2 > minPercent && percent_2 < maxPercent) {
                        if (isRow && !existingRows.some(function (p) { return Math.abs(p - percent_2) < 0.001; })) {
                            existingRows.push(percent_2);
                        }
                        else if (!isRow && !existingCols.some(function (p) { return Math.abs(p - percent_2) < 0.001; })) {
                            existingCols.push(percent_2);
                        }
                    }
                    newData = __assign(__assign({}, oldData), { openCuts: { rows: existingRows, cols: existingCols }, partColors: openWasDeleted
                            ? remapColorsAfterCutRemoval(oldData.partColors, isRow ? 'row' : 'col', openDragCutIdx)
                            : oldData.partColors });
                    setDraggingExistingCut(null);
                    // On touch: tap (no drag) resets state and lets onClick handle kbCutMode.
                    if (IS_TOUCH_DEVICE && !((_w = cutDragRef.current) === null || _w === void 0 ? void 0 : _w.hasMoved)) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                    // Skip save if nothing actually changed
                    var unchanged = JSON.stringify((_y = (_x = oldData.openCuts) === null || _x === void 0 ? void 0 : _x.rows) !== null && _y !== void 0 ? _y : []) === JSON.stringify(existingRows) &&
                        JSON.stringify((_0 = (_z = oldData.openCuts) === null || _z === void 0 ? void 0 : _z.cols) !== null && _0 !== void 0 ? _0 : []) === JSON.stringify(existingCols);
                    if (unchanged) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                }
                else {
                    // ── New cut in open mode ──
                    // Reject cuts too close to edges
                    if (percent_2 <= minPercent || percent_2 >= maxPercent) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                    var existingOpenRows = (_2 = (_1 = oldData.openCuts) === null || _1 === void 0 ? void 0 : _1.rows) !== null && _2 !== void 0 ? _2 : [];
                    var existingOpenCols = (_4 = (_3 = oldData.openCuts) === null || _3 === void 0 ? void 0 : _3.cols) !== null && _4 !== void 0 ? _4 : [];
                    var maxOpenCuts = 4;
                    var existingOpenAlongAxis = currentCutPreview.type === 'row' ? existingOpenRows : existingOpenCols;
                    var sortedOpenAlongAxis = __spreadArray([], existingOpenAlongAxis, true).sort(function (a, b) { return a - b; });
                    var insertPosOpen = sortedOpenAlongAxis.filter(function (c) { return c < percent_2; }).length;
                    newData = __assign(__assign({}, oldData), { openCuts: {
                            rows: currentCutPreview.type === 'row'
                                ? existingOpenRows.length < maxOpenCuts
                                    ? Array.from(new Set(__spreadArray(__spreadArray([], existingOpenRows, true), [percent_2], false)))
                                    : existingOpenRows
                                : existingOpenRows,
                            cols: currentCutPreview.type === 'col'
                                ? existingOpenCols.length < maxOpenCuts
                                    ? Array.from(new Set(__spreadArray(__spreadArray([], existingOpenCols, true), [percent_2], false)))
                                    : existingOpenCols
                                : existingOpenCols,
                        }, partColors: remapColorsAfterCutAddition(oldData.partColors, currentCutPreview.type, insertPosOpen) });
                }
            }
            // Apply the computed new data
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'arrayModels'
                        ? __assign(__assign({}, item), { data: newData }) : item;
                });
            });
            // Play cut sound when a line is placed
            playAudio('scissor');
            // Announce cut for screen readers (WCAG 4.1.3)
            if (mode === 'grid') {
                var snappedAnnounce = Math.round(currentCutPreview.value / cellSize);
                var limitAnnounce = currentCutPreview.type === 'row' ? oldData.rows : oldData.columns;
                if (existingCutValue != null && (snappedAnnounce < 1 || snappedAnnounce >= limitAnnounce)) {
                    announce(t('text.arrayModelCutRemoved'));
                }
                else {
                    announce(currentCutPreview.type === 'row'
                        ? t('text.arrayModelRowDivided', { position: snappedAnnounce })
                        : t('text.arrayModelColumnDivided', { position: snappedAnnounce }));
                }
            }
            else {
                var openPercent = currentCutPreview.type === 'row'
                    ? currentCutPreview.value / height
                    : currentCutPreview.value / width;
                announce(currentCutPreview.type === 'row'
                    ? t('text.arrayModelRowDivided', { position: Math.round(openPercent * 100) + '%' })
                    : t('text.arrayModelColumnDivided', { position: Math.round(openPercent * 100) + '%' }));
            }
            // Save state synchronously with pre-computed values
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData),
            });
            // Select this array and open the right panel — the user just placed a cut on it
            setSelectedTool([data.id]);
            customEventHandler.dispatchEvent('openRightDrawer', {});
            cutDragRef.current = null;
            setCutPreview(null);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
        return function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [
        mode,
        scale,
        zoomLevel,
        cellSize,
        data.rows,
        data.columns,
        width,
        height,
        data.id,
        setManipulative,
        setKbCutMode,
        t,
    ]);
    /* ================= LABELS & EXPRESSIONS (GRID ONLY) ================= */
    var colSegments = useMemo(function () {
        if (mode !== 'grid')
            return [];
        // Account for preview during dragging
        var cols = __spreadArray([], cutColumns, true);
        if (cutPreview && cutPreview.type === 'col' && draggingExistingCut) {
            // Remove the old cut being dragged
            cols = cols.filter(function (c) { return c !== draggingExistingCut.value; });
            // Add the preview position (snapped)
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.columns && !cols.includes(snapped)) {
                cols.push(snapped);
            }
            cols.sort(function (a, b) { return a - b; });
        }
        else if (cutPreview && !draggingExistingCut && cutPreview.type === 'col') {
            // Adding a new cut - include preview
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.columns && !cols.includes(snapped)) {
                cols.push(snapped);
                cols.sort(function (a, b) { return a - b; });
            }
        }
        var pts = __spreadArray(__spreadArray([0], cols, true), [data.columns], false);
        return pts.slice(1).map(function (v, i) { return v - pts[i]; });
    }, [mode, cutColumns, data.columns, cellSize, cutPreview, draggingExistingCut]);
    var rowSegments = useMemo(function () {
        if (mode !== 'grid')
            return [];
        // Account for preview during dragging
        var rows = __spreadArray([], cutRows, true);
        if (cutPreview && cutPreview.type === 'row' && draggingExistingCut) {
            // Remove the old cut being dragged
            rows = rows.filter(function (r) { return r !== draggingExistingCut.value; });
            // Add the preview position (snapped)
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.rows && !rows.includes(snapped)) {
                rows.push(snapped);
            }
            rows.sort(function (a, b) { return a - b; });
        }
        else if (cutPreview && !draggingExistingCut && cutPreview.type === 'row') {
            // Adding a new cut - include preview
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.rows && !rows.includes(snapped)) {
                rows.push(snapped);
                rows.sort(function (a, b) { return a - b; });
            }
        }
        var pts = __spreadArray(__spreadArray([0], rows, true), [data.rows], false);
        return pts.slice(1).map(function (v, i) { return v - pts[i]; });
    }, [mode, cutRows, data.rows, cellSize, cutPreview, draggingExistingCut]);
    var isOpen = mode === 'open';
    // Committed-only cut points (no preview). Used exclusively for color-block
    // rendering so that section indices — and therefore partColors keys — remain
    // stable while the user is dragging or previewing a new cut.
    var rowCutPointsStable = useMemo(function () {
        var _a, _b;
        if (!isOpen)
            return __spreadArray(__spreadArray([0], __spreadArray([], cutRows, true).sort(function (a, b) { return a - b; }), true), [data.rows], false);
        return __spreadArray(__spreadArray([0], __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []), true).sort(function (a, b) { return a - b; }), true), [1], false);
    }, [isOpen, cutRows, data.rows, data.openCuts]);
    var colCutPointsStable = useMemo(function () {
        var _a, _b;
        if (!isOpen)
            return __spreadArray(__spreadArray([0], __spreadArray([], cutColumns, true).sort(function (a, b) { return a - b; }), true), [data.columns], false);
        return __spreadArray(__spreadArray([0], __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.cols) !== null && _b !== void 0 ? _b : []), true).sort(function (a, b) { return a - b; }), true), [1], false);
    }, [isOpen, cutColumns, data.columns, data.openCuts]);
    //const isOpen = mode === 'open';
    var openRowCuts = (_q = (_p = data.openCuts) === null || _p === void 0 ? void 0 : _p.rows) !== null && _q !== void 0 ? _q : [];
    var openColCuts = (_s = (_r = data.openCuts) === null || _r === void 0 ? void 0 : _r.cols) !== null && _s !== void 0 ? _s : [];
    var openRowPoints = React.useMemo(function () {
        if (!isOpen)
            return [];
        return __spreadArray(__spreadArray([0], openRowCuts, true), [1], false);
    }, [isOpen, openRowCuts]);
    var openColPoints = React.useMemo(function () {
        if (!isOpen)
            return [];
        return __spreadArray(__spreadArray([0], openColCuts, true), [1], false);
    }, [isOpen, openColCuts]);
    var products = useMemo(function () {
        var list = [];
        rowSegments.forEach(function (r) { return colSegments.forEach(function (c) { return list.push(r * c); }); });
        return list;
    }, [rowSegments, colSegments]);
    var total = products.reduce(function (a, b) { return a + b; }, 0);
    // Screen-reader-friendly text for expression and product
    var expressionReadableText = useMemo(function () {
        if (mode !== 'grid')
            return '';
        var parts = [];
        rowSegments.forEach(function (r) {
            colSegments.forEach(function (c) {
                parts.push("(".concat(r, " times ").concat(c, ")"));
            });
        });
        return "".concat(parts.join(' + '), " = (").concat(data.rows, " times ").concat(data.columns, ")");
    }, [mode, rowSegments, colSegments, data.rows, data.columns]);
    var productReadableText = useMemo(function () {
        if (mode !== 'grid')
            return '';
        return "".concat(products.join(' + '), " = ").concat(total);
    }, [mode, products, total]);
    // Announce expression and product when they change (row/column add, cut line)
    var prevExpressionRef = useRef(expressionReadableText);
    var prevProductRef = useRef(productReadableText);
    var isInitialRenderRef = useRef(true);
    useEffect(function () {
        // Skip initial render — don't announce on mount
        if (isInitialRenderRef.current) {
            isInitialRenderRef.current = false;
            prevExpressionRef.current = expressionReadableText;
            prevProductRef.current = productReadableText;
            return;
        }
        if (!isFocused || isPreview || mode !== 'grid')
            return;
        var parts = [];
        if (showExpression && expressionReadableText !== prevExpressionRef.current) {
            parts.push("".concat(t('text.arrayModelShowExpression'), ": ").concat(expressionReadableText));
        }
        if (showProduct && productReadableText !== prevProductRef.current) {
            parts.push("".concat(t('text.arrayModelShowProduct'), ": ").concat(productReadableText));
        }
        prevExpressionRef.current = expressionReadableText;
        prevProductRef.current = productReadableText;
        if (parts.length > 0) {
            // Delay slightly so the primary action announcement (e.g. "Row added") is read first
            setTimeout(function () { return announce(parts.join('. ')); }, 300);
        }
    }, [expressionReadableText, productReadableText, showExpression, showProduct, isFocused, isPreview, mode, t]);
    // Calculate visual label data for smooth dragging (grid mode only)
    var rowLabelData = useMemo(function () {
        if (mode !== 'grid')
            return [];
        // Get cut positions - use preview position if dragging
        var cuts = __spreadArray([], cutRows, true);
        var previewPos = null;
        if (cutPreview && cutPreview.type === 'row') {
            if (draggingExistingCut) {
                // Remove old cut
                cuts = cuts.filter(function (r) { return r !== draggingExistingCut.value; });
            }
            // Use actual preview pixel position for smooth movement
            previewPos = cutPreview.value;
        }
        // Build cut points in pixels
        var cutPoints = __spreadArray(__spreadArray([0], cuts.map(function (r) { return r * cellSize; }), true), [data.rows * cellSize], false);
        // If dragging, insert preview position for smooth visual positioning
        if (previewPos !== null) {
            cutPoints.push(previewPos);
            cutPoints.sort(function (a, b) { return a - b; });
        }
        // Calculate label data (height and value)
        return cutPoints.slice(0, -1).map(function (start, i) {
            var end = cutPoints[i + 1];
            var height = end - start;
            var value = Math.round(height / cellSize); // Snapped value for display
            return { height: height, value: value };
        });
    }, [mode, cutRows, data.rows, cellSize, cutPreview, draggingExistingCut]);
    var colLabelData = useMemo(function () {
        if (mode !== 'grid')
            return [];
        // Get cut positions - use preview position if dragging
        var cuts = __spreadArray([], cutColumns, true);
        var previewPos = null;
        if (cutPreview && cutPreview.type === 'col') {
            if (draggingExistingCut) {
                // Remove old cut
                cuts = cuts.filter(function (c) { return c !== draggingExistingCut.value; });
            }
            // Use actual preview pixel position for smooth movement
            previewPos = cutPreview.value;
        }
        // Build cut points in pixels
        var cutPoints = __spreadArray(__spreadArray([0], cuts.map(function (c) { return c * cellSize; }), true), [data.columns * cellSize], false);
        // If dragging, insert preview position for smooth visual positioning
        if (previewPos !== null) {
            cutPoints.push(previewPos);
            cutPoints.sort(function (a, b) { return a - b; });
        }
        // Calculate label data (width and value)
        return cutPoints.slice(0, -1).map(function (start, i) {
            var end = cutPoints[i + 1];
            var width = end - start;
            var value = Math.round(width / cellSize); // Snapped value for display
            return { width: width, value: value };
        });
    }, [mode, cutColumns, data.columns, cellSize, cutPreview, draggingExistingCut]);
    var openRegions = React.useMemo(function () {
        var _a, _b, _c, _d;
        if (mode !== 'open')
            return [];
        // Start with the current cuts
        var rowCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []), true);
        var colCuts = __spreadArray([], ((_d = (_c = data.openCuts) === null || _c === void 0 ? void 0 : _c.cols) !== null && _d !== void 0 ? _d : []), true);
        // If dragging an existing cut, update the arrays to reflect the preview position
        if (cutPreview && draggingExistingCut) {
            var previewPercent = cutPreview.type === 'row' ? cutPreview.value / height : cutPreview.value / width;
            if (cutPreview.type === 'row') {
                // Remove the old cut being dragged
                rowCuts = rowCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
                // Add the preview position if it's valid
                if (previewPercent > 0 && previewPercent < 1) {
                    rowCuts.push(previewPercent);
                }
            }
            else {
                // Remove the old cut being dragged
                colCuts = colCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
                // Add the preview position if it's valid
                if (previewPercent > 0 && previewPercent < 1) {
                    colCuts.push(previewPercent);
                }
            }
        }
        else if (cutPreview && !draggingExistingCut) {
            // Adding a new cut - include the preview position
            var previewPercent = cutPreview.type === 'row' ? cutPreview.value / height : cutPreview.value / width;
            if (cutPreview.type === 'row' && previewPercent > 0 && previewPercent < 1) {
                rowCuts.push(previewPercent);
            }
            else if (cutPreview.type === 'col' && previewPercent > 0 && previewPercent < 1) {
                colCuts.push(previewPercent);
            }
        }
        // Add boundaries and sort
        var finalRowCuts = __spreadArray(__spreadArray([0], rowCuts.sort(function (a, b) { return a - b; }), true), [1], false);
        var finalColCuts = __spreadArray(__spreadArray([0], colCuts.sort(function (a, b) { return a - b; }), true), [1], false);
        var regions = [];
        for (var r = 0; r < finalRowCuts.length - 1; r++) {
            for (var c = 0; c < finalColCuts.length - 1; c++) {
                var x = finalColCuts[c] * width;
                var y = finalRowCuts[r] * height;
                var w = (finalColCuts[c + 1] - finalColCuts[c]) * width;
                var h = (finalRowCuts[r + 1] - finalRowCuts[r]) * height;
                regions.push({
                    x: x,
                    y: y,
                    w: w,
                    h: h,
                    key: "open-".concat(r, "-").concat(c),
                });
            }
        }
        return regions;
    }, [mode, data.openCuts, width, height, cutPreview, draggingExistingCut]);
    var renderedRowCuts = useMemo(function () {
        var _a, _b;
        if (mode === 'grid') {
            return cutRows.map(function (r) { return r * cellSize; });
        }
        // Open mode - account for dragging
        var rowCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []), true);
        if (cutPreview && cutPreview.type === 'row' && draggingExistingCut) {
            // Remove the old cut being dragged
            rowCuts = rowCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
        }
        return rowCuts.map(function (p) { return p * height; });
    }, [mode, cutRows, data.openCuts, cellSize, height, cutPreview, draggingExistingCut]);
    var renderedColCuts = useMemo(function () {
        var _a, _b;
        if (mode === 'grid') {
            return cutColumns.map(function (c) { return c * cellSize; });
        }
        // Open mode - account for dragging
        var colCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.cols) !== null && _b !== void 0 ? _b : []), true);
        if (cutPreview && cutPreview.type === 'col' && draggingExistingCut) {
            // Remove the old cut being dragged
            colCuts = colCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
        }
        return colCuts.map(function (p) { return p * width; });
    }, [mode, cutColumns, data.openCuts, cellSize, width, cutPreview, draggingExistingCut]);
    var onPartClick = function () {
        var _a;
        if (partModeEnabledRef.current) {
            setSelectedPartKey(null);
        }
        var newEnabled = !((_a = dataRef.current.partModeEnabled) !== null && _a !== void 0 ? _a : true);
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'arrayModels'
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { partModeEnabled: newEnabled }) }) : item;
            });
        });
        var partsMsg = newEnabled ? t('text.arrayModelPartsOn') : t('text.arrayModelPartsOff');
        setAssertiveText('');
        requestAnimationFrame(function () { return setAssertiveText(partsMsg); });
    };
    // When partMode is turned ON (false → true), focus the cut arrow so the user can
    // immediately start placing a cut. useEffect guarantees the arrows are in the DOM.
    var prevPartModeEnabledRef = useRef(partModeEnabled);
    useEffect(function () {
        var _a, _b;
        var wasOff = !prevPartModeEnabledRef.current;
        prevPartModeEnabledRef.current = partModeEnabled;
        if (!partModeEnabled || !wasOff)
            return;
        var target = ((_a = cutArrowRowRef.current) === null || _a === void 0 ? void 0 : _a.tabIndex) !== -1
            ? cutArrowRowRef.current
            : ((_b = cutArrowColRef.current) === null || _b === void 0 ? void 0 : _b.tabIndex) !== -1
                ? cutArrowColRef.current
                : null;
        target === null || target === void 0 ? void 0 : target.focus();
    }, [partModeEnabled]);
    var onSelectClick = function () {
        if (selectModeEnabledRef.current) {
            setSelectedPartKey(null);
        }
        var newEnabled = !selectModeEnabledRef.current;
        setSelectModeEnabled(function (prev) { return !prev; });
        if (newEnabled) {
            // Move focus to the array so user can Tab into selectable sections
            announce(t('text.arrayModelSelectOn'));
            requestAnimationFrame(function () {
                var _a;
                (_a = arrayInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            });
        }
        else {
            announce(t('text.arrayModelSelectOff'));
        }
    };
    var updateArray = function (updater) {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'arrayModels'
                    ? __assign(__assign({}, item), { data: updater(item.data) }) : item;
            });
        });
    };
    // ================= RESIZE HANDLERS (OPEN MODE) =================
    var onResizeStart = function (e, corner) {
        if (mode !== 'open')
            return;
        e.stopPropagation();
        e.preventDefault();
        var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        resizeDragRef.current = {
            corner: corner,
            startX: x,
            startY: y,
            startRows: data.rows,
            startCols: data.columns,
            startCenterX: data.center.x,
            startCenterY: data.center.y,
        };
    };
    // Set up resize drag listeners
    useEffect(function () {
        var onResizeMove = function (e) {
            if (!resizeDragRef.current)
                return;
            var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var _a = resizeDragRef.current, corner = _a.corner, startX = _a.startX, startY = _a.startY, startRows = _a.startRows, startCols = _a.startCols, startCenterX = _a.startCenterX, startCenterY = _a.startCenterY;
            // Calculate delta in pixels (canvas coordinates)
            var deltaX = (x - startX) / (scale * zoomLevel);
            var deltaY = (y - startY) / (scale * zoomLevel);
            var cellsX = deltaX / workspaceCellSize;
            var cellsY = deltaY / workspaceCellSize;
            var newRows = startRows;
            var newCols = startCols;
            if (corner === 'bottomRight') {
                newRows = Math.max(1, startRows + cellsY);
                newCols = Math.max(1, startCols + cellsX);
            }
            else if (corner === 'topLeft') {
                newRows = Math.max(1, startRows - cellsY);
                newCols = Math.max(1, startCols - cellsX);
            }
            // Adjust center so the opposite corner stays fixed
            var startW = startCols * workspaceCellSize;
            var startH = startRows * workspaceCellSize;
            var newW = newCols * workspaceCellSize;
            var newH = newRows * workspaceCellSize;
            var newCenterX = startCenterX;
            var newCenterY = startCenterY;
            if (corner === 'bottomRight') {
                // Fix top-left corner
                newCenterX = startCenterX + ((newW - startW) * scale) / 2;
                newCenterY = startCenterY + ((newH - startH) * scale) / 2;
            }
            else if (corner === 'topLeft') {
                // Fix bottom-right corner
                newCenterX = startCenterX - ((newW - startW) * scale) / 2;
                newCenterY = startCenterY - ((newH - startH) * scale) / 2;
            }
            // Update dimensions and center
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.id === data.id && item.data.type === 'arrayModels') {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rows: newRows, columns: newCols, center: { x: newCenterX, y: newCenterY } }) });
                    }
                    return item;
                });
            });
        };
        var onResizeUp = function () {
            if (!resizeDragRef.current)
                return;
            var oldData = {
                rows: resizeDragRef.current.startRows,
                columns: resizeDragRef.current.startCols,
            };
            var newData = {
                rows: data.rows,
                columns: data.columns,
            };
            // Only save state if dimensions actually changed
            if (oldData.rows !== newData.rows || oldData.columns !== newData.columns) {
                var oldDataFull = structuredClone(dataRef.current);
                oldDataFull.rows = oldData.rows;
                oldDataFull.columns = oldData.columns;
                oldDataFull.center = {
                    x: resizeDragRef.current.startCenterX,
                    y: resizeDragRef.current.startCenterY,
                };
                var newDataFull = structuredClone(dataRef.current);
                saveState({
                    type: data.id,
                    id: data.id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(oldDataFull),
                    new: JSON.stringify(newDataFull),
                });
                // Announce resize for screen readers (WCAG 4.1.3)
                announce(t('text.arrayModelResized', {
                    rows: Math.round(newData.rows),
                    columns: Math.round(newData.columns),
                }));
            }
            resizeDragRef.current = null;
        };
        window.addEventListener('mousemove', onResizeMove);
        window.addEventListener('mouseup', onResizeUp);
        window.addEventListener('touchmove', onResizeMove);
        window.addEventListener('touchend', onResizeUp);
        return function () {
            window.removeEventListener('mousemove', onResizeMove);
            window.removeEventListener('mouseup', onResizeUp);
            window.removeEventListener('touchmove', onResizeMove);
            window.removeEventListener('touchend', onResizeUp);
        };
    }, [scale, zoomLevel, cellSize, data.id, mode, setManipulative]);
    var addRow = function () {
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        var newData = __assign(__assign({}, oldData), { rows: oldData.rows + 1 });
        updateArray(function (d) { return (__assign(__assign({}, d), { rows: d.rows + 1 })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        var rowMsg = t('text.arrayModelRowAdded', { total: oldData.rows + 1 });
        setAssertiveText('');
        requestAnimationFrame(function () { return setAssertiveText(rowMsg); });
    };
    var addColumn = function () {
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        var newData = __assign(__assign({}, oldData), { columns: oldData.columns + 1 });
        updateArray(function (d) { return (__assign(__assign({}, d), { columns: d.columns + 1 })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        var colMsg = t('text.arrayModelColumnAdded', { total: oldData.columns + 1 });
        setAssertiveText('');
        requestAnimationFrame(function () { return setAssertiveText(colMsg); });
    };
    var removeRow = function () {
        var _a, _b;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        if (oldData.rows <= 1)
            return;
        var newRows = oldData.rows - 1;
        // Remove any cuts at/beyond the new boundary and remap colors
        var newCutRows = ((_a = oldData.cutRows) !== null && _a !== void 0 ? _a : []).filter(function (r) { return r < newRows; });
        var newColors = __assign({}, ((_b = oldData.partColors) !== null && _b !== void 0 ? _b : {}));
        Object.keys(newColors).forEach(function (key) {
            var rowIdx = parseInt(key.split('-')[0], 10);
            if (rowIdx >= newRows)
                delete newColors[key];
        });
        var newData = __assign(__assign({}, oldData), { rows: newRows, cutRows: newCutRows, partColors: newColors });
        updateArray(function (d) { return (__assign(__assign({}, d), { rows: newRows, cutRows: newCutRows, partColors: newColors })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        var removeRowMsg = t('text.arrayModelRowsDecreased', { count: newRows });
        setAssertiveText('');
        requestAnimationFrame(function () { return setAssertiveText(removeRowMsg); });
    };
    var removeColumn = function () {
        var _a, _b;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        if (oldData.columns <= 1)
            return;
        var newCols = oldData.columns - 1;
        // Remove any cuts at/beyond the new boundary and remap colors
        var newCutColumns = ((_a = oldData.cutColumns) !== null && _a !== void 0 ? _a : []).filter(function (c) { return c < newCols; });
        var newColors = __assign({}, ((_b = oldData.partColors) !== null && _b !== void 0 ? _b : {}));
        Object.keys(newColors).forEach(function (key) {
            var colIdx = parseInt(key.split('-')[1], 10);
            if (colIdx >= newCols)
                delete newColors[key];
        });
        var newData = __assign(__assign({}, oldData), { columns: newCols, cutColumns: newCutColumns, partColors: newColors });
        updateArray(function (d) { return (__assign(__assign({}, d), { columns: newCols, cutColumns: newCutColumns, partColors: newColors })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        var removeColMsg = t('text.arrayModelColumnsDecreased', { count: newCols });
        setAssertiveText('');
        requestAnimationFrame(function () { return setAssertiveText(removeColMsg); });
    };
    var toggleExpression = function () {
        var _a;
        var oldData = structuredClone(dataRef.current);
        var newVal = !((_a = oldData.showExpression) !== null && _a !== void 0 ? _a : true);
        var newData = __assign(__assign({}, oldData), { showExpression: newVal });
        updateArray(function (d) { return (__assign(__assign({}, d), { showExpression: newVal })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        var exprMsg = newVal ? t('text.arrayModelExpressionOn') : t('text.arrayModelExpressionOff');
        setAssertiveText('');
        requestAnimationFrame(function () {
            var _a;
            setAssertiveText(exprMsg);
            if (newVal)
                (_a = expressionRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    var toggleProduct = function () {
        var _a;
        var oldData = structuredClone(dataRef.current);
        var newVal = !((_a = oldData.showProduct) !== null && _a !== void 0 ? _a : true);
        var newData = __assign(__assign({}, oldData), { showProduct: newVal });
        updateArray(function (d) { return (__assign(__assign({}, d), { showProduct: newVal })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        var prodMsg = newVal ? t('text.arrayModelProductOn') : t('text.arrayModelProductOff');
        setAssertiveText('');
        requestAnimationFrame(function () {
            var _a;
            setAssertiveText(prodMsg);
            if (newVal)
                (_a = productRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    var openRowCutsCount = ((_u = (_t = data.openCuts) === null || _t === void 0 ? void 0 : _t.rows) !== null && _u !== void 0 ? _u : []).length;
    var openColCutsCount = ((_w = (_v = data.openCuts) === null || _v === void 0 ? void 0 : _v.cols) !== null && _w !== void 0 ? _w : []).length;
    var rowCutsMaxed = mode === 'grid'
        ? cutRows.length >= Math.min(data.rows - 1, 4)
        : mode === 'open' && openRowCutsCount >= 4;
    var colCutsMaxed = mode === 'grid'
        ? cutColumns.length >= Math.min(data.columns - 1, 4)
        : mode === 'open' && openColCutsCount >= 4;
    // Disabled states for cut move panel buttons.
    var cutPanelDisabled = (function () {
        if (!kbCutMode)
            return { back: true, fwd: true, confirm: true };
        var isRow = kbCutMode.type === 'row';
        var pos = kbCutMode.position;
        if (mode === 'grid') {
            var limit = isRow ? data.rows : data.columns;
            var existing = isRow ? cutRows : cutColumns;
            var canBack = false;
            for (var i = pos - 1; i >= 1; i--) {
                if (!existing.includes(i)) {
                    canBack = true;
                    break;
                }
            }
            var canFwd = false;
            for (var i = pos + 1; i < limit; i++) {
                if (!existing.includes(i)) {
                    canFwd = true;
                    break;
                }
            }
            return { back: !canBack, fwd: !canFwd, confirm: existing.includes(pos) };
        }
        else {
            var dimension = isRow ? height : width;
            var step = dimension * 0.1;
            return {
                back: pos - step <= 0,
                fwd: pos + step >= dimension,
                confirm: false,
            };
        }
    })();
    /* ================= KEYBOARD HANDLER (following Dice pattern) ================= */
    var handleKeyDown = useCallback(function (e) {
        var _a, _b, _c;
        if (isPreview)
            return;
        // Context menu: Enter, Space, Shift+F10, ContextMenu key
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            if (!selectedTool.includes(data.id)) {
                setSelectedTool([data.id]);
            }
            var rect = (_a = arrayInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({
                    x: rect.right,
                    y: rect.top,
                    manipulativeId: data.id,
                });
            }
            return;
        }
        // Arrow key movement (when not position-locked)
        if (!panningEnable &&
            !((_b = data.lock) === null || _b === void 0 ? void 0 : _b.positioning) &&
            ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            var curPos = (_c = keyMoveCurPosRef.current) !== null && _c !== void 0 ? _c : data.center;
            if (!keyMoveStartRef.current) {
                keyMoveStartRef.current = __assign({}, curPos);
            }
            var step = e.shiftKey ? 50 : 10;
            var scaledStep = step / zoomLevel;
            var delta = {
                ArrowUp: { x: 0, y: -scaledStep },
                ArrowDown: { x: 0, y: scaledStep },
                ArrowLeft: { x: -scaledStep, y: 0 },
                ArrowRight: { x: scaledStep, y: 0 },
            };
            var d = delta[e.key];
            var newCenter_1 = { x: curPos.x + d.x, y: curPos.y + d.y };
            keyMoveCurPosRef.current = newCenter_1;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_1 }) }) : item;
                });
            });
            // Debounced undo save + screen reader announcement
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = setTimeout(function () {
                if (keyMoveStartRef.current && keyMoveCurPosRef.current) {
                    var oldPos = keyMoveStartRef.current;
                    var newPos_1 = keyMoveCurPosRef.current;
                    saveState({
                        type: data.id,
                        id: data.id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(oldPos),
                        new: JSON.stringify(newPos_1),
                    });
                    var dx = Math.round(newPos_1.x - oldPos.x);
                    var dy = Math.round(newPos_1.y - oldPos.y);
                    var directions_1 = [];
                    if (dy < 0)
                        directions_1.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                    if (dy > 0)
                        directions_1.push("".concat(t('text.down'), " ").concat(dy));
                    if (dx < 0)
                        directions_1.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                    if (dx > 0)
                        directions_1.push("".concat(t('text.right'), " ").concat(dx));
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        setAnnounceText(t('text.manipulativeMoved', {
                            name: t('text.arrayModels'),
                            direction: directions_1.join(" ".concat(t('text.and'), " ")),
                            x: Math.round(newPos_1.x),
                            y: Math.round(newPos_1.y),
                        }));
                    });
                    keyMoveStartRef.current = null;
                    keyMoveCurPosRef.current = null;
                }
            }, 500);
        }
        else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            // Prevent arrow keys from scrolling even when movement is locked
            e.preventDefault();
        }
    }, [isPreview, selectedTool, data.id, data.center, (_x = data.lock) === null || _x === void 0 ? void 0 : _x.positioning, panningEnable, zoomLevel, setManipulative, setSelectedTool, setContextMenu, t]);
    /* ================= ARRAY ORDINAL (distinguish multiple arrays) ================= */
    var arrayNumber = useMemo(function () {
        var arrays = manipulative.filter(function (m) { return m.data.type === 'arrayModels'; });
        var idx = arrays.findIndex(function (m) { return m.data.id === data.id; });
        return idx === -1 ? -1 : idx + 1;
    }, [manipulative, data.id]);
    /* ================= ARIA LABEL ================= */
    var ariaLabel = useMemo(function () {
        var _a, _b, _c, _d, _e;
        if (duplicateLabel)
            return duplicateLabel;
        if (creationLabel)
            return creationLabel;
        var modeLabel = mode === 'grid' ? t('text.arrayModelGridArray') : t('text.arrayModelOpenArray');
        var gridDesc = t('text.arrayModelAriaGridDesc', { rows: data.rows, columns: data.columns });
        // Ordinal to distinguish multiple arrays
        var ordinal = arrayNumber > 0 ? "".concat(arrayNumber, ". ") : '';
        // Current state summary
        var stateParts = [];
        var totalRowCuts = mode === 'grid' ? cutRows.length : ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []).length;
        var totalColCuts = mode === 'grid' ? cutColumns.length : ((_d = (_c = data.openCuts) === null || _c === void 0 ? void 0 : _c.cols) !== null && _d !== void 0 ? _d : []).length;
        if (totalRowCuts > 0 || totalColCuts > 0) {
            var rowParts = totalRowCuts + 1;
            var colParts = totalColCuts + 1;
            stateParts.push(t('text.arrayModelPartsSummary', { rowParts: rowParts, colParts: colParts }));
        }
        if (selectModeEnabled) {
            stateParts.push(t('text.arrayModelSelectOn'));
        }
        var coloredCount = Object.keys((_e = data.partColors) !== null && _e !== void 0 ? _e : {}).filter(function (k) { var _a; return (_a = data.partColors) === null || _a === void 0 ? void 0 : _a[k]; }).length;
        if (coloredCount > 0) {
            stateParts.push(t('text.arrayModelColoredSections', { count: coloredCount }));
        }
        var stateStr = stateParts.length > 0 ? ". ".concat(stateParts.join('. ')) : '';
        var hint = !isPreview
            ? selectModeEnabled
                ? ". ".concat(t('text.arrayModelTabToSelectHint'))
                : ". ".concat(t('text.arrayModelPressEnterForActions'))
            : '';
        return "".concat(ordinal).concat(modeLabel, ". ").concat(gridDesc).concat(stateStr).concat(hint);
    }, [duplicateLabel, creationLabel, mode, data.rows, data.columns, data.openCuts, data.partColors, cutRows, cutColumns, selectModeEnabled, arrayNumber, isPreview, t]);
    /* ================= RENDER ================= */
    return (_jsxs("div", { className: "arrayModelWrapper", "data-manipulative-id": data.id, ref: arrayInteractiveRef, tabIndex: isPreview ? -1 : 0, "aria-roledescription": t('text.arrayModelRoledescription'), "aria-label": ariaLabel, onKeyDown: !isPreview ? handleKeyDown : undefined, onFocus: !isPreview ? function (e) {
            if (e.target !== arrayInteractiveRef.current)
                return;
            if (!selectedTool.includes(data.id)) {
                setSelectedTool([data.id]);
            }
            // Outer div receiving direct focus means the user interacted with the array
            // (tap, Tab key, programmatic re-focus after context-menu close). Open the
            // right panel in case it was closed while the array remained selected.
            customEventHandler.dispatchEvent('openRightDrawer', {});
        } : undefined, style: {
            position: isPreview ? 'relative' : 'absolute',
            left: isPreview ? undefined : data.center.x,
            top: isPreview ? undefined : data.center.y,
            transform: isPreview ? 'none' : "translate(-50%, -50%) scale(".concat(scale, ")"),
            boxShadow: !isPreview && isFocused ? '0 0 20px 0 #5063AE' : 'none',
        }, children: [_jsx(SelectFocusOverlay, { id: data.id, hidden: isPreview, ariaLabel: 'text.arrayModels', onAfterClick: IS_TOUCH_DEVICE ? function () {
                    var _a;
                    var rect = (_a = arrayInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                    if (rect)
                        setContextMenu({ x: rect.right, y: rect.top, manipulativeId: data.id });
                } : undefined }), !isPreview && mode === 'grid' && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: {
                            position: 'absolute',
                            right: '100%',
                            marginRight: 15,
                            top: 0,
                            height: height,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            fontSize: 26,
                            fontWeight: 700,
                            fontFamily: 'oso-sans, sans-serif',
                            color: '#293273',
                            pointerEvents: 'none',
                        }, children: rowLabelData.map(function (labelData, i) { return (_jsx("div", { style: {
                                height: labelData.height,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, children: labelData.value }, i)); }) }), _jsx("div", { "aria-hidden": "true", style: {
                            position: 'absolute',
                            bottom: '100%',
                            marginBottom: 9,
                            left: 0,
                            width: width,
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 26,
                            fontWeight: 700,
                            fontFamily: 'oso-sans, sans-serif',
                            color: '#293273',
                            pointerEvents: 'none',
                        }, children: colLabelData.map(function (labelData, i) { return (_jsx("div", { style: {
                                width: labelData.width,
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                            }, children: labelData.value }, i)); }) })] })), _jsxs("div", { className: "arrayModel", onMouseDown: onGridMouseDown, onTouchStart: onGridMouseDown, style: {
                    width: width,
                    height: height,
                    position: 'relative',
                    background: '#fff',
                    display: mode === 'grid' ? 'grid' : 'block',
                    gridTemplateColumns: mode === 'grid' ? "repeat(".concat(data.columns, ", ").concat(cellSize, "px)") : undefined,
                    gridTemplateRows: mode === 'grid' ? "repeat(".concat(data.rows, ", ").concat(cellSize, "px)") : undefined,
                    boxSizing: 'content-box',
                }, children: [rowCutPointsStable.slice(0, -1).map(function (rStart, rIdx) {
                        return colCutPointsStable.slice(0, -1).map(function (cStart, cIdx) {
                            var _a, _b, _c;
                            var rEnd = rowCutPointsStable[rIdx + 1];
                            var cEnd = colCutPointsStable[cIdx + 1];
                            var key = "".concat(rIdx, "-").concat(cIdx);
                            var bg = (_a = data.partColors) === null || _a === void 0 ? void 0 : _a[key];
                            // ✅ GRID MODE → cell math
                            var left = isOpen ? cStart * width : cStart * cellSize;
                            var top = isOpen ? rStart * height : rStart * cellSize;
                            var w = isOpen ? (cEnd - cStart) * width : (cEnd - cStart) * cellSize;
                            var h = isOpen ? (rEnd - rStart) * height : (rEnd - rStart) * cellSize;
                            var isSelected = selectedPartKey === key;
                            var sectionLabel = bg
                                ? t('text.arrayModelSectionWithColor', { row: rIdx + 1, col: cIdx + 1, color: (_c = (_b = Object.keys(ColorEnum).find(function (k) { return ColorEnum[k] === bg; })) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== null && _c !== void 0 ? _c : '' })
                                : t('text.arrayModelSectionLabel', { row: rIdx + 1, col: cIdx + 1 });
                            return (_jsx("div", { role: selectModeEnabled ? 'button' : undefined, tabIndex: selectModeEnabled ? 0 : undefined, "aria-label": selectModeEnabled ? "".concat(sectionLabel).concat(isSelected ? ", ".concat(t('text.selected')) : '') : undefined, "aria-pressed": selectModeEnabled ? isSelected : undefined, onClick: function (e) {
                                    if (!selectModeEnabled)
                                        return;
                                    // Skip iOS synthesized click — handled via onUp after drag detection
                                    if (Date.now() - lastRegionTouchRef.current < 300)
                                        return;
                                    e.stopPropagation();
                                    // Ensure array is the active manipulative and right panel is open
                                    if (!selectedTool.includes(data.id)) {
                                        setSelectedTool([data.id]);
                                    }
                                    customEventHandler.dispatchEvent('openRightDrawer', {});
                                    var newKey = selectedPartKey === key ? null : key;
                                    setSelectedPartKey(function (prev) { return (prev === key ? null : key); });
                                    // Announce selection for screen readers
                                    if (newKey) {
                                        announce(t('text.arrayModelSectionSelected', { row: rIdx + 1, col: cIdx + 1 }));
                                    }
                                    else {
                                        announce(t('text.arrayModelSectionDeselected'));
                                    }
                                }, onKeyDown: selectModeEnabled ? function (e) {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // Ensure array is the active manipulative and right panel is open
                                        if (!selectedTool.includes(data.id)) {
                                            setSelectedTool([data.id]);
                                        }
                                        customEventHandler.dispatchEvent('openRightDrawer', {});
                                        var newKey = selectedPartKey === key ? null : key;
                                        setSelectedPartKey(function (prev) { return (prev === key ? null : key); });
                                        if (newKey) {
                                            announce(t('text.arrayModelSectionSelected', { row: rIdx + 1, col: cIdx + 1 }));
                                        }
                                        else {
                                            announce(t('text.arrayModelSectionDeselected'));
                                        }
                                    }
                                } : undefined, onTouchStart: function (e) {
                                    if (!selectModeEnabled)
                                        return;
                                    // Record which region was touched; let onGridMouseDown handle drag detection
                                    // Selection is applied in onUp only if the touch didn't become a drag
                                    lastRegionTouchRef.current = Date.now();
                                    touchedRegionKeyRef.current = key;
                                }, style: {
                                    position: 'absolute',
                                    left: left,
                                    top: top,
                                    width: w,
                                    height: h,
                                    backgroundColor: bg ? bg : isSelected ? 'rgba(41, 50, 115, 0.15)' : 'transparent',
                                    opacity: bg ? 0.5 : 1,
                                    cursor: selectModeEnabled ? 'pointer' : 'default',
                                    zIndex: 1,
                                    border: isSelected ? (bg ? '5px solid #293273' : '3px solid #293273') : 'none',
                                    boxSizing: 'border-box',
                                } }, key));
                        });
                    }), mode === 'open' &&
                        openRegions.map(function (region) { return (_jsx("div", { "aria-hidden": !selectModeEnabled, style: {
                                position: 'absolute',
                                left: region.x,
                                top: region.y,
                                width: region.w,
                                height: region.h,
                                boxSizing: 'border-box',
                                cursor: selectModeEnabled ? 'pointer' : 'default',
                            } }, region.key)); }), mode === 'open' && !isPreview && (_jsxs(_Fragment, { children: [_jsx("div", { role: "button", tabIndex: 0, "aria-label": t('text.arrayModelResizeTopLeft'), style: {
                                    position: 'absolute',
                                    left: -8,
                                    top: -8,
                                    width: 16,
                                    height: 16,
                                    background: '#fff',
                                    border: '1px solid #000',
                                    cursor: 'nwse-resize',
                                    zIndex: 10,
                                    boxSizing: 'border-box',
                                }, onMouseDown: function (e) { return onResizeStart(e, 'topLeft'); }, onTouchStart: function (e) { return onResizeStart(e, 'topLeft'); }, onKeyDown: function (e) {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        var step = e.shiftKey ? 5 : 1;
                                        var oldData = structuredClone(dataRef.current);
                                        var newRows_1 = e.key === 'ArrowUp' ? Math.max(1, data.rows + step) : data.rows;
                                        var newCols_1 = e.key === 'ArrowLeft' ? Math.max(1, data.columns + step) : data.columns;
                                        updateArray(function (d) { return (__assign(__assign({}, d), { rows: newRows_1, columns: newCols_1 })); });
                                        saveState({ type: data.id, id: data.id.split('_')[1], propertyName: 'completeData', old: JSON.stringify(oldData), new: JSON.stringify(__assign(__assign({}, oldData), { rows: newRows_1, columns: newCols_1 })) });
                                        announce(t('text.arrayModelResized', { rows: Math.round(newRows_1), columns: Math.round(newCols_1) }));
                                    }
                                    else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        var step = e.shiftKey ? 5 : 1;
                                        var oldData = structuredClone(dataRef.current);
                                        var newRows_2 = e.key === 'ArrowDown' ? Math.max(1, data.rows - step) : data.rows;
                                        var newCols_2 = e.key === 'ArrowRight' ? Math.max(1, data.columns - step) : data.columns;
                                        updateArray(function (d) { return (__assign(__assign({}, d), { rows: newRows_2, columns: newCols_2 })); });
                                        saveState({ type: data.id, id: data.id.split('_')[1], propertyName: 'completeData', old: JSON.stringify(oldData), new: JSON.stringify(__assign(__assign({}, oldData), { rows: newRows_2, columns: newCols_2 })) });
                                        announce(t('text.arrayModelResized', { rows: Math.round(newRows_2), columns: Math.round(newCols_2) }));
                                    }
                                } }), _jsx("div", { role: "button", tabIndex: 0, "aria-label": t('text.arrayModelResizeBottomRight'), style: {
                                    position: 'absolute',
                                    right: -8,
                                    bottom: -8,
                                    width: 16,
                                    height: 16,
                                    background: '#fff',
                                    border: '1px solid #000',
                                    cursor: 'nwse-resize',
                                    zIndex: 10,
                                    boxSizing: 'border-box',
                                }, onMouseDown: function (e) { return onResizeStart(e, 'bottomRight'); }, onTouchStart: function (e) { return onResizeStart(e, 'bottomRight'); }, onKeyDown: function (e) {
                                    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        var step = e.shiftKey ? 5 : 1;
                                        var oldData = structuredClone(dataRef.current);
                                        var newRows_3 = e.key === 'ArrowDown' ? data.rows + step : data.rows;
                                        var newCols_3 = e.key === 'ArrowRight' ? data.columns + step : data.columns;
                                        updateArray(function (d) { return (__assign(__assign({}, d), { rows: newRows_3, columns: newCols_3 })); });
                                        saveState({ type: data.id, id: data.id.split('_')[1], propertyName: 'completeData', old: JSON.stringify(oldData), new: JSON.stringify(__assign(__assign({}, oldData), { rows: newRows_3, columns: newCols_3 })) });
                                        announce(t('text.arrayModelResized', { rows: Math.round(newRows_3), columns: Math.round(newCols_3) }));
                                    }
                                    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        var step = e.shiftKey ? 5 : 1;
                                        var oldData = structuredClone(dataRef.current);
                                        var newRows_4 = e.key === 'ArrowUp' ? Math.max(1, data.rows - step) : data.rows;
                                        var newCols_4 = e.key === 'ArrowLeft' ? Math.max(1, data.columns - step) : data.columns;
                                        updateArray(function (d) { return (__assign(__assign({}, d), { rows: newRows_4, columns: newCols_4 })); });
                                        saveState({ type: data.id, id: data.id.split('_')[1], propertyName: 'completeData', old: JSON.stringify(oldData), new: JSON.stringify(__assign(__assign({}, oldData), { rows: newRows_4, columns: newCols_4 })) });
                                        announce(t('text.arrayModelResized', { rows: Math.round(newRows_4), columns: Math.round(newCols_4) }));
                                    }
                                } })] })), mode === 'grid' &&
                        Array.from({ length: data.rows * data.columns }).map(function (_, i) { return (_jsx("div", { className: "cell", "aria-hidden": "true" }, i)); }), _jsx("div", { className: "gridBorderOverlay", "aria-hidden": "true", style: {
                            position: 'absolute',
                            inset: 0,
                            border: '5px solid #4586C0',
                            zIndex: 2,
                            pointerEvents: 'none',
                        } }), renderedRowCuts.map(function (y, i) {
                        // Get the actual cut value (index for grid, percentage for open)
                        // For open mode, derive from pixel position since renderedRowCuts is already filtered
                        var cutValue = mode === 'grid' ? cutRows[i] : y / height;
                        // Hide the cut being re-dragged (it becomes the preview)
                        if ((draggingExistingCut === null || draggingExistingCut === void 0 ? void 0 : draggingExistingCut.type) === 'row') {
                            if (mode === 'grid' && cutRows[i] === draggingExistingCut.value)
                                return null;
                        }
                        return (_jsxs(React.Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: {
                                        position: 'absolute',
                                        top: y - 1,
                                        left: 0,
                                        width: '100%',
                                        height: 3,
                                        background: '#293273',
                                        zIndex: 5,
                                    } }), _jsx("div", { role: "button", tabIndex: 0, "aria-disabled": !partModeEnabled, "aria-label": mode === 'grid'
                                        ? t(IS_TOUCH_DEVICE ? 'text.arrayModelExistingCutRowTouch' : 'text.arrayModelExistingCutRow', { position: cutValue })
                                        : t(IS_TOUCH_DEVICE ? 'text.arrayModelExistingCutRowTouch' : 'text.arrayModelExistingCutRow', { position: Math.round(cutValue * 100) + '%' }), style: {
                                        position: 'absolute',
                                        top: y - 17,
                                        right: -47,
                                        width: 35,
                                        height: 35,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: partModeEnabled ? 'auto' : 'none',
                                        cursor: partModeEnabled ? 'ns-resize' : 'default',
                                        zIndex: 6,
                                        touchAction: 'none',
                                    }, onMouseDown: function (e) {
                                        if (IS_TOUCH_DEVICE)
                                            return;
                                        onExistingCutStart(e, 'row', cutValue);
                                    }, onTouchStart: function (e) { return onExistingCutTouchStart(e, 'row', cutValue); }, onClick: IS_TOUCH_DEVICE ? function () {
                                        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                                        // Fires for both VoiceOver double-tap and regular tap (after onUp resets).
                                        // Not called for drag — iOS suppresses click after a touchmove sequence.
                                        if (!partModeEnabled)
                                            return;
                                        var oldData = structuredClone(dataRef.current);
                                        var removedRows = __spreadArray([], ((_a = oldData.cutRows) !== null && _a !== void 0 ? _a : []), true);
                                        var removedOpen = __spreadArray([], ((_c = (_b = oldData.openCuts) === null || _b === void 0 ? void 0 : _b.rows) !== null && _c !== void 0 ? _c : []), true);
                                        var newData;
                                        if (mode === 'grid') {
                                            var i_1 = removedRows.indexOf(cutValue);
                                            if (i_1 !== -1)
                                                removedRows.splice(i_1, 1);
                                            var sortedBefore = __spreadArray([], ((_d = oldData.cutRows) !== null && _d !== void 0 ? _d : []), true).sort(function (a, b) { return a - b; });
                                            var removalIdx = sortedBefore.indexOf(cutValue);
                                            newData = __assign(__assign({}, oldData), { cutRows: removedRows, cutColumns: (_e = oldData.cutColumns) !== null && _e !== void 0 ? _e : [], partColors: remapColorsAfterCutRemoval(oldData.partColors, 'row', removalIdx) });
                                            setKbCutMode({ type: 'row', position: cutValue });
                                        }
                                        else {
                                            var i_2 = removedOpen.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                                            if (i_2 !== -1)
                                                removedOpen.splice(i_2, 1);
                                            var sortedBefore = __spreadArray([], ((_g = (_f = oldData.openCuts) === null || _f === void 0 ? void 0 : _f.rows) !== null && _g !== void 0 ? _g : []), true).sort(function (a, b) { return a - b; });
                                            var removalIdx = sortedBefore.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                                            newData = __assign(__assign({}, oldData), { openCuts: { rows: removedOpen, cols: (_j = (_h = oldData.openCuts) === null || _h === void 0 ? void 0 : _h.cols) !== null && _j !== void 0 ? _j : [] }, partColors: remapColorsAfterCutRemoval(oldData.partColors, 'row', removalIdx) });
                                            setKbCutMode({ type: 'row', position: cutValue * height });
                                        }
                                        setManipulative(function (prev) { return prev.map(function (item) {
                                            return item.data.id === data.id && item.data.type === 'arrayModels' ? __assign(__assign({}, item), { data: newData }) : item;
                                        }); });
                                        saveState({ type: data.id, id: data.id.split('_')[1], propertyName: 'completeData',
                                            old: JSON.stringify(oldData), new: JSON.stringify(newData) });
                                    } : undefined, onKeyDown: function (e) { return handleExistingCutKeyDown(e, 'row', cutValue); }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "\n      M17 4\n      C18 4 18 5 18 6\n      V18\n      C18 19 18 20 17 20\n      C16.5 20 16 19.7 15 18.9\n      L8.5 13.5\n      C7.8 12.9 7.8 11.1 8.5 10.5\n      L15 5.1\n      C16 4.3 16.5 4 17 4\n      Z\n    ", fill: "currentColor" }) }) })] }, "row-cut-".concat(i)));
                    }), renderedColCuts.map(function (x, i) {
                        // Get the actual cut value (index for grid, percentage for open)
                        // For open mode, derive from pixel position since renderedColCuts is already filtered
                        var cutValue = mode === 'grid' ? cutColumns[i] : x / width;
                        // Hide the cut being re-dragged (it becomes the preview)
                        if ((draggingExistingCut === null || draggingExistingCut === void 0 ? void 0 : draggingExistingCut.type) === 'col') {
                            if (mode === 'grid' && cutColumns[i] === draggingExistingCut.value)
                                return null;
                        }
                        return (_jsxs(React.Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: {
                                        position: 'absolute',
                                        left: x - 1,
                                        top: 0,
                                        width: 3,
                                        height: '100%',
                                        background: '#293273',
                                        zIndex: 5,
                                    } }), _jsx("div", { role: "button", tabIndex: 0, "aria-disabled": !partModeEnabled, "aria-label": mode === 'grid'
                                        ? t(IS_TOUCH_DEVICE ? 'text.arrayModelExistingCutColTouch' : 'text.arrayModelExistingCutCol', { position: cutValue })
                                        : t(IS_TOUCH_DEVICE ? 'text.arrayModelExistingCutColTouch' : 'text.arrayModelExistingCutCol', { position: Math.round(cutValue * 100) + '%' }), style: {
                                        position: 'absolute',
                                        left: x - 17,
                                        bottom: -45,
                                        width: 35,
                                        height: 35,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: partModeEnabled ? 'auto' : 'none',
                                        cursor: partModeEnabled ? 'ew-resize' : 'default',
                                        zIndex: 6,
                                        touchAction: 'none',
                                    }, onMouseDown: function (e) {
                                        if (IS_TOUCH_DEVICE)
                                            return;
                                        onExistingCutStart(e, 'col', cutValue);
                                    }, onTouchStart: function (e) { return onExistingCutTouchStart(e, 'col', cutValue); }, onClick: IS_TOUCH_DEVICE ? function () {
                                        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                                        if (!partModeEnabled)
                                            return;
                                        var oldData = structuredClone(dataRef.current);
                                        var removedCols = __spreadArray([], ((_a = oldData.cutColumns) !== null && _a !== void 0 ? _a : []), true);
                                        var removedOpen = __spreadArray([], ((_c = (_b = oldData.openCuts) === null || _b === void 0 ? void 0 : _b.cols) !== null && _c !== void 0 ? _c : []), true);
                                        var newData;
                                        if (mode === 'grid') {
                                            var i_3 = removedCols.indexOf(cutValue);
                                            if (i_3 !== -1)
                                                removedCols.splice(i_3, 1);
                                            var sortedBefore = __spreadArray([], ((_d = oldData.cutColumns) !== null && _d !== void 0 ? _d : []), true).sort(function (a, b) { return a - b; });
                                            var removalIdx = sortedBefore.indexOf(cutValue);
                                            newData = __assign(__assign({}, oldData), { cutRows: (_e = oldData.cutRows) !== null && _e !== void 0 ? _e : [], cutColumns: removedCols, partColors: remapColorsAfterCutRemoval(oldData.partColors, 'col', removalIdx) });
                                            setKbCutMode({ type: 'col', position: cutValue });
                                        }
                                        else {
                                            var i_4 = removedOpen.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                                            if (i_4 !== -1)
                                                removedOpen.splice(i_4, 1);
                                            var sortedBefore = __spreadArray([], ((_g = (_f = oldData.openCuts) === null || _f === void 0 ? void 0 : _f.cols) !== null && _g !== void 0 ? _g : []), true).sort(function (a, b) { return a - b; });
                                            var removalIdx = sortedBefore.findIndex(function (p) { return Math.abs(p - cutValue) < 0.001; });
                                            newData = __assign(__assign({}, oldData), { openCuts: { rows: (_j = (_h = oldData.openCuts) === null || _h === void 0 ? void 0 : _h.rows) !== null && _j !== void 0 ? _j : [], cols: removedOpen }, partColors: remapColorsAfterCutRemoval(oldData.partColors, 'col', removalIdx) });
                                            setKbCutMode({ type: 'col', position: cutValue * width });
                                        }
                                        setManipulative(function (prev) { return prev.map(function (item) {
                                            return item.data.id === data.id && item.data.type === 'arrayModels' ? __assign(__assign({}, item), { data: newData }) : item;
                                        }); });
                                        saveState({ type: data.id, id: data.id.split('_')[1], propertyName: 'completeData',
                                            old: JSON.stringify(oldData), new: JSON.stringify(newData) });
                                    } : undefined, onKeyDown: function (e) { return handleExistingCutKeyDown(e, 'col', cutValue); }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "\n      M4 17\n      C4 18 5 18 6 18\n      H18\n      C19 18 20 18 20 17\n      C20 16.5 19.7 16 18.9 15\n      L13.5 8.5\n      C12.9 7.8 11.1 7.8 10.5 8.5\n      L5.1 15\n      C4.3 16 4 16.5 4 17\n      Z\n    ", fill: "currentColor" }) }) })] }, "col-cut-".concat(i)));
                    }), cutPreview && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: {
                                    position: 'absolute',
                                    top: cutPreview.type === 'row' ? cutPreview.value - 1 : 0,
                                    left: cutPreview.type === 'col' ? cutPreview.value - 1 : 0,
                                    width: cutPreview.type === 'row' ? '100%' : 3,
                                    height: cutPreview.type === 'col' ? '100%' : 3,
                                    background: '#ff9800',
                                    zIndex: 7,
                                } }), cutPreview.type === 'row' && (_jsx("div", { "aria-hidden": "true", style: {
                                    position: 'absolute',
                                    top: cutPreview.value - 17,
                                    right: -47,
                                    width: 35,
                                    height: 35,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                    zIndex: 8,
                                }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "\n      M17 4\n      C18 4 18 5 18 6\n      V18\n      C18 19 18 20 17 20\n      C16.5 20 16 19.7 15 18.9\n      L8.5 13.5\n      C7.8 12.9 7.8 11.1 8.5 10.5\n      L15 5.1\n      C16 4.3 16.5 4 17 4\n      Z\n    ", fill: "#ff9800", stroke: "#ff9800" }) }) })), cutPreview.type === 'col' && (_jsx("div", { "aria-hidden": "true", style: {
                                    position: 'absolute',
                                    left: cutPreview.value - 17,
                                    bottom: -45,
                                    width: 35,
                                    height: 35,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                    zIndex: 8,
                                }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "\n      M4 17\n      C4 18 5 18 6 18\n      H18\n      C19 18 20 18 20 17\n      C20 16.5 19.7 16 18.9 15\n      L13.5 8.5\n      C12.9 7.8 11.1 7.8 10.5 8.5\n      L5.1 15\n      C4.3 16 4 16.5 4 17\n      Z\n    ", fill: "#ff9800", stroke: "#ff9800" }) }) }))] }))] }), !isPreview && partModeEnabled && (_jsxs(_Fragment, { children: [_jsxs("div", { ref: cutArrowRowRef, className: "cutArrow cutArrowRight", role: "button", tabIndex: rowCutsMaxed ? -1 : 0, "aria-label": (kbCutMode === null || kbCutMode === void 0 ? void 0 : kbCutMode.type) === 'row'
                            ? t('text.arrayModelCutPlacing', { type: t('text.rows') })
                            : t('text.arrayModelCutArrowRow'), "aria-disabled": rowCutsMaxed ? true : undefined, "aria-hidden": rowCutsMaxed ? true : undefined, onMouseDown: function (e) {
                            if (IS_TOUCH_DEVICE)
                                return;
                            !rowCutsMaxed && onCutStart(e, 'row');
                        }, onTouchStart: function (e) {
                            // Always start the touch drag; onUp handles tap-with-no-movement → kbCutMode
                            !rowCutsMaxed && onCutStart(e, 'row');
                        }, onMouseEnter: function () { return !rowCutsMaxed && setHoveredArrow('row'); }, onMouseLeave: function () { return setHoveredArrow(null); }, onFocus: function () { return !rowCutsMaxed && setHoveredArrow('row'); }, onBlur: function () { return setHoveredArrow(null); }, onKeyDown: function (e) {
                            if (rowCutsMaxed)
                                return;
                            if ((kbCutMode === null || kbCutMode === void 0 ? void 0 : kbCutMode.type) === 'row') {
                                handleKbCutKeyDown(e, 'row');
                            }
                            else if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                var startPos = mode === 'grid' ? 1 : height / 2;
                                setKbCutMode({ type: 'row', position: startPos });
                                var posLabel = mode === 'grid' ? '1' : '50%';
                                announce(t('text.arrayModelCutStarted', { type: t('text.rows'), position: posLabel }));
                            }
                        }, style: rowCutsMaxed
                            ? {
                                opacity: 0.3,
                                cursor: 'default',
                                pointerEvents: 'none',
                                background: '#293273',
                                color: '#fff',
                                border: 'none',
                            }
                            : {}, children: [_jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "\n      M17 4\n      C18 4 18 5 18 6\n      V18\n      C18 19 18 20 17 20\n      C16.5 20 16 19.7 15 18.9\n      L8.5 13.5\n      C7.8 12.9 7.8 11.1 8.5 10.5\n      L15 5.1\n      C16 4.3 16.5 4 17 4\n      Z\n    ", fill: "currentColor" }) }), hoveredArrow === 'row' && (_jsx("div", { className: "cutArrowTooltip", "aria-hidden": "true", children: t('text.arrayModelCreateParts') }))] }), _jsxs("div", { ref: cutArrowColRef, className: "cutArrow cutArrowBottom", role: "button", tabIndex: colCutsMaxed ? -1 : 0, "aria-label": (kbCutMode === null || kbCutMode === void 0 ? void 0 : kbCutMode.type) === 'col'
                            ? t('text.arrayModelCutPlacing', { type: t('text.columns') })
                            : t('text.arrayModelCutArrowCol'), "aria-disabled": colCutsMaxed ? true : undefined, "aria-hidden": colCutsMaxed ? true : undefined, onMouseDown: function (e) {
                            if (IS_TOUCH_DEVICE)
                                return;
                            !colCutsMaxed && onCutStart(e, 'col');
                        }, onTouchStart: function (e) {
                            // Always start the touch drag; onUp handles tap-with-no-movement → kbCutMode
                            !colCutsMaxed && onCutStart(e, 'col');
                        }, onMouseEnter: function () { return !colCutsMaxed && setHoveredArrow('col'); }, onMouseLeave: function () { return setHoveredArrow(null); }, onFocus: function () { return !colCutsMaxed && setHoveredArrow('col'); }, onBlur: function () { return setHoveredArrow(null); }, onKeyDown: function (e) {
                            if (colCutsMaxed)
                                return;
                            if ((kbCutMode === null || kbCutMode === void 0 ? void 0 : kbCutMode.type) === 'col') {
                                handleKbCutKeyDown(e, 'col');
                            }
                            else if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                var startPos = mode === 'grid' ? 1 : width / 2;
                                setKbCutMode({ type: 'col', position: startPos });
                                var posLabel = mode === 'grid' ? '1' : '50%';
                                announce(t('text.arrayModelCutStarted', { type: t('text.columns'), position: posLabel }));
                            }
                        }, style: colCutsMaxed
                            ? {
                                opacity: 0.3,
                                cursor: 'default',
                                pointerEvents: 'none',
                                background: '#293273',
                                color: '#fff',
                                border: 'none',
                            }
                            : {}, children: [_jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "\n      M4 17\n      C4 18 5 18 6 18\n      H18\n      C19 18 20 18 20 17\n      C20 16.5 19.7 16 18.9 15\n      L13.5 8.5\n      C12.9 7.8 11.1 7.8 10.5 8.5\n      L5.1 15\n      C4.3 16 4 16.5 4 17\n      Z\n    ", fill: "currentColor" }) }), hoveredArrow === 'col' && (_jsx("div", { className: "cutArrowTooltip", "aria-hidden": "true", children: t('text.arrayModelCreateParts') }))] })] })), !isPreview && kbCutMode && (_jsxs("div", { ref: cutMovePanelRef, className: "cutMovePanel", role: "group", onKeyDown: function (e) { return e.stopPropagation(); }, "aria-label": kbCutMode.type === 'row'
                    ? t('text.arrayModelMoveCutRow', { defaultValue: 'Move horizontal cut' })
                    : t('text.arrayModelMoveCutCol', { defaultValue: 'Move vertical cut' }), children: [kbCutMode.type === 'row' ? (
                    /* Horizontal cut → Up / Down */
                    _jsxs(_Fragment, { children: [_jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveUp', { defaultValue: 'Move up' }), disabled: cutPanelDisabled.back, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(-1); }, children: "\u25B2" }), _jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveDown', { defaultValue: 'Move down' }), disabled: cutPanelDisabled.fwd, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(1); }, children: "\u25BC" })] })) : (
                    /* Vertical cut → Left / Right */
                    _jsxs(_Fragment, { children: [_jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveLeft', { defaultValue: 'Move left' }), disabled: cutPanelDisabled.back, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(-1); }, children: "\u25C0" }), _jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveRight', { defaultValue: 'Move right' }), disabled: cutPanelDisabled.fwd, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(1); }, children: "\u25B6" })] })), _jsx("button", { className: "cutMoveBtn cutMoveBtnConfirm", "aria-label": t('text.confirm', { defaultValue: 'Confirm' }), disabled: cutPanelDisabled.confirm, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); commitKbCut(); }, children: "\u2713" }), _jsx("button", { className: "cutMoveBtn cutMoveBtnCancel", "aria-label": t('text.cancel', { defaultValue: 'Cancel' }), onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); setKbCutMode(null); setCutPreview(null); }, children: "\u2715" })] })), !isPreview && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0, 0, 0, 0)',
                            whiteSpace: 'nowrap',
                            border: 0,
                        }, children: announceText }), _jsx("div", { "aria-live": "assertive", "aria-atomic": "true", role: "alert", style: {
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0, 0, 0, 0)',
                            whiteSpace: 'nowrap',
                            border: 0,
                        }, children: assertiveText })] })), mode === 'grid' && !isPreview && (showProduct || showExpression) && (_jsxs("div", { role: "region", "aria-label": t('text.arrayModelExpressionArea'), style: {
                    position: 'absolute',
                    top: height + 70,
                    left: width / 2,
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: 22,
                    fontFamily: 'oso-sans, sans-serif',
                    color: '#293273',
                    pointerEvents: 'none',
                }, children: [showExpression && (_jsx("div", { ref: expressionRef, tabIndex: 0, role: "group", "aria-label": "".concat(t('text.arrayModelShowExpression'), ": ").concat(expressionReadableText), style: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: '6px',
                            lineHeight: 1.8,
                            marginBottom: 8,
                            width: width + width / 3,
                            pointerEvents: 'auto',
                        }, children: (function () {
                            var parts = [];
                            var idx = 0;
                            rowSegments.forEach(function (r, rIdx) {
                                colSegments.forEach(function (c, cIdx) {
                                    var _a;
                                    if (idx > 0)
                                        parts.push(' + ');
                                    var partColor = (_a = data.partColors) === null || _a === void 0 ? void 0 : _a["".concat(rIdx, "-").concat(cIdx)];
                                    parts.push(_jsxs("span", { "aria-hidden": "true", style: partColor ? { color: partColor } : undefined, children: ["(", r, " \u00D7 ", c, ")"] }, "expr-".concat(rIdx, "-").concat(cIdx)));
                                    idx++;
                                });
                            });
                            return (_jsxs(_Fragment, { children: [parts, _jsxs("span", { "aria-hidden": "true", children: [" =", ' ', _jsxs("b", { children: ["(", data.rows, " \u00D7 ", data.columns, ")"] })] })] }));
                        })() })), showProduct && (_jsx("div", { className: "expressionHolder", style: { textAlign: 'center' }, children: _jsx("div", { ref: productRef, tabIndex: 0, role: "group", "aria-label": "".concat(t('text.arrayModelShowProduct'), ": ").concat(productReadableText), style: {
                                display: 'inline-block',
                                maxWidth: '100%',
                                padding: '10px 17px',
                                border: '1px solid #5063ae',
                                fontWeight: 700,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                borderRadius: 5,
                                pointerEvents: 'auto',
                            }, children: (function () {
                                var parts = [];
                                var flatIdx = 0;
                                rowSegments.forEach(function (_, rIdx) {
                                    colSegments.forEach(function (_, cIdx) {
                                        var _a;
                                        if (flatIdx > 0)
                                            parts.push(' + ');
                                        var partColor = (_a = data.partColors) === null || _a === void 0 ? void 0 : _a["".concat(rIdx, "-").concat(cIdx)];
                                        parts.push(_jsx("span", { "aria-hidden": "true", style: partColor ? { color: partColor } : undefined, children: products[flatIdx] }, "prod-".concat(rIdx, "-").concat(cIdx)));
                                        flatIdx++;
                                    });
                                });
                                return (_jsxs(_Fragment, { children: [parts, _jsxs("span", { "aria-hidden": "true", children: [" = ", total] })] }));
                            })() }) }))] }))] }));
};
export default ArrayModel;
